import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useData } from '../providers/DataProvider';
import { Filter, Plus, Search, SlidersHorizontal, X, ChevronRight, ChevronDown, Calendar, Download } from 'lucide-react';
import { exportToCSV, exportToXLSX } from '../lib/exporters';
import { useNotifications } from './Notifications';
import Pagination from './ui/Pagination';
import Skeleton from './ui/Skeleton';
import EmptyState from './ui/EmptyState';
import StateMessage from './ui/StateMessage';
import ConfirmDialog from './ui/ConfirmDialog';
import { cn } from '../lib/cn';
import { computeOrderChargeBreakdown, computeSaleChargeBreakdown, getOrderChargesArray, resolveChargeKind } from '../lib/orderCharges';
import { validateTransactionPayload } from '../lib/validators';

const createDefaultDraftCharge = () => ({
  kind: 'external',
  label: 'Comisión externa',
  value: '',
  type: 'flat',
});

const createEmptyAddDepositModal = () => ({ show: false, transactionId: null, amount: '', date: '', reference: '', proofFile: null });
const createEmptyDepositHistoryModal = () => ({ show: false, transaction: null });
const createEmptyDeliveryModal = () => ({ show: false, transactionId: null, amount: '' });
const createEmptyDeliveryConfirmModal = () => ({ show: false, expected: null, received: null });

const Transactions = ({
  transactions = [],
  clients = [],
  suppliers = [],
  brokers = [],
  branches = [],
  isLoading = false,
  onAddTransaction = () => {},
  onEditTransaction = () => {},
  onDeleteTransaction = () => {},
  onUpdateTransactionDeposits = () => {},
  onMarkAsDelivered = () => {},
  onCancelOrder = () => {},
  onToggleCommission = () => {},
}) => {
  const { push } = useNotifications ? useNotifications() : { push: () => {} };
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);
  const [sortKey, setSortKey] = useState('date');
  const [sortDir, setSortDir] = useState('desc');
  // Date range filtering
  const [dateRangeType, setDateRangeType] = useState('all'); // all | today | 7d | 14d | 30d | 90d | custom
  const [customStart, setCustomStart] = useState(''); // yyyy-mm-dd
  const [customEnd, setCustomEnd] = useState(''); // yyyy-mm-dd
  // Additional chip filters
  const [selectedBranchIds, setSelectedBranchIds] = useState([]); // array of string ids
  const [selectedClientIds, setSelectedClientIds] = useState([]);
  const [selectedSupplierIds, setSelectedSupplierIds] = useState([]);
  const [selectedBrokerIds, setSelectedBrokerIds] = useState([]);
  const [commissionPaidFilter, setCommissionPaidFilter] = useState('all'); // all | paid | pending
  const [amountMin, setAmountMin] = useState('');
  const [amountMax, setAmountMax] = useState('');
  const [depositAccountFilter, setDepositAccountFilter] = useState('');
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [wizardStep, setWizardStep] = useState(1);
  const [newTransaction, setNewTransaction] = useState({ type: 'sale', amount: '', clientId: '', supplierId: '', brokerId: '', brokerPercentage: '', branchId: '', sourceBranchId: '', targetBranchId: '', depositAccount: 'Firsbel', depositAmount: '', costAmount: '', purchasePercent: '', orderCharges: [], saleCharges: [] });
  const [editingTransactionId, setEditingTransactionId] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [viewOnlyMode, setViewOnlyMode] = useState(false);
  const [savingTx, setSavingTx] = useState(false);
  const validationErrors = useMemo(() => validateTransactionPayload(newTransaction), [newTransaction]);
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [saleChargesTouched, setSaleChargesTouched] = useState(false);
  // Draft inputs for adding a new order fee/charge as a chip
  const [draftCharge, setDraftCharge] = useState(() => createDefaultDraftCharge());
  const feesContainerRef = useRef(null);
  // Inline popover editor state for existing chips
  const [editingChargeIndex, setEditingChargeIndex] = useState(null);
  const [editChargeDraft, setEditChargeDraft] = useState(() => createDefaultDraftCharge());
  const [popoverPos, setPopoverPos] = useState({ top: 0, left: 0 });
  const popoverRef = useRef(null);
  const [showFeesEditor, setShowFeesEditor] = useState(false);
  const [clientSearchTerm, setClientSearchTerm] = useState('');
  const [showClientDropdown, setShowClientDropdown] = useState(false);

  const getChargesKeyFor = (tx) => ((tx?.type || '') === 'sale' ? 'saleCharges' : 'orderCharges');
  const getActiveCharges = (tx = newTransaction) => {
    const key = getChargesKeyFor(tx);
    const list = Array.isArray(tx?.[key]) ? tx[key] : [];
    return list;
  };
  const setActiveCharges = (updater) => {
    setNewTransaction((prev) => {
      const key = getChargesKeyFor(prev);
      const prevList = Array.isArray(prev[key]) ? prev[key] : [];
      const nextListRaw = typeof updater === 'function' ? updater(prevList, prev) : updater;
      const nextList = Array.isArray(nextListRaw) ? nextListRaw : [];
      return { ...prev, [key]: nextList };
    });
  };
  const activeCharges = getActiveCharges();

  const openAddChargeEditor = () => {
    setDraftCharge((prev) => {
      const nextKind = newTransaction.type === 'sale' ? 'internal' : (prev.kind || 'external');
      const nextType = newTransaction.type === 'sale' ? 'percent' : (prev.type || 'flat');
      return {
        ...prev,
        kind: nextKind,
        label: nextKind === 'internal' ? 'Comisión interna' : 'Comisión externa',
        type: nextType,
        value: '',
      };
    });
    setShowFeesEditor(true);
  };

  const buildChargeId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;

  const buildInternalPercentCharge = (value, { label } = {}) => ({
    id: buildChargeId(),
    type: 'percent',
    value: Number(value) || 0,
    mode: 'on_top',
    kind: 'internal',
    label: label || 'Comisión interna',
  });

  const ensureClientDefaultSaleCharges = (client) => {
    if (!client) return [];
    if (Array.isArray(client.defaultSaleCharges) && client.defaultSaleCharges.length > 0) {
      return client.defaultSaleCharges.map((charge) => ({
        id: charge.id || buildChargeId(),
        type: charge.type || 'percent',
        value: Number(charge.value || 0) || 0,
        mode: charge.mode || 'on_top',
        kind: charge.kind || (String(charge.label || '').toLowerCase().includes('interna') ? 'internal' : 'external'),
        label: charge.label || ((charge.kind === 'internal') ? 'Comisión interna' : 'Comisión externa'),
      }));
    }
    const legacy = Number(client.defaultSalePercentage ?? client.default_sale_percentage ?? 0) || 0;
    if (legacy) {
      return [buildInternalPercentCharge(legacy)];
    }
    return [];
  };

  const ensureSaleChargesFrom = (tx) => {
    if (!tx) return [];
    const existing = [
      Array.isArray(tx.saleCharges) ? tx.saleCharges : null,
      Array.isArray(tx.sale_charges) ? tx.sale_charges : null,
      Array.isArray(tx.metadata?.saleCharges) ? tx.metadata.saleCharges : null,
    ].find((arr) => Array.isArray(arr) && arr.length > 0);
    if (existing) {
      return existing.map((c, idx) => ({
        id: c.id || buildChargeId(),
        type: c.type || 'flat',
        value: c.value != null ? Number(c.value) : 0,
        mode: c.mode || 'on_top',
        kind: c.kind || (String(c.label || '').toLowerCase().includes('interna') ? 'internal' : 'external'),
        label: c.label || ((c.kind === 'internal') ? 'Comisión interna' : 'Comisión externa'),
      }));
    }
    const legacyPct = Number(tx.salePercentage ?? tx.sale_percentage ?? 0) || 0;
    if (legacyPct) {
      return [buildInternalPercentCharge(legacyPct)];
    }
    return [];
  };

  const renderChargesEditor = () => {
    const type = newTransaction.type;
    if (!['sale', 'order', 'transfer'].includes(type)) return null;
    const isSale = type === 'sale';
    const title = isSale
      ? 'Cargos de venta'
      : type === 'transfer'
        ? 'Cargos de transferencia'
        : 'Cargos de compra';
  const addButtonLabel = isSale ? 'Agregar comisión' : 'Agregar cargo';
  const emptyCta = addButtonLabel;
    const tipText = isSale
      ? 'Tip: Presiona Enter o sal del módulo para confirmar el cargo. Comisión interna representa tu comisión; externa suma cargos adicionales.'
      : 'Tip: Presiona Enter o sal del módulo para confirmar el cargo. Comisión externa suma; comisión interna descuenta.';
    
    if (viewOnlyMode && activeCharges.length === 0) {
      return (
        <div className="space-y-2">
          <div className="text-sm font-medium text-gray-700 dark:text-gray-200">{title}</div>
          <div className="text-sm text-gray-500 dark:text-gray-400">No hay cargos registrados</div>
        </div>
      );
    }
    
    return (
      <div className="space-y-2" ref={feesContainerRef}>
        <div className="text-sm font-medium text-gray-700 dark:text-gray-200">{title}</div>
        <div className="flex flex-wrap gap-2">
          {activeCharges.map((ch, idx) => {
            const isPercent = (ch.type || 'flat') === 'percent';
            const val = Number(ch.value || 0) || 0;
            const kind = ch.kind || (String(ch.label || '').toLowerCase().includes('interna') ? 'internal' : 'external');
            const text = `${kind === 'internal' ? 'Comisión interna' : 'Comisión externa'} · ${isPercent ? `${val}%` : `$${val.toLocaleString('en-US')}`}`;
            return (
              <span
                key={ch.id || `${idx}`}
                className={`inline-flex items-center gap-2 rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-700 dark:bg-gray-700 dark:text-gray-200 ${viewOnlyMode ? '' : 'cursor-pointer'}`}
                onClick={viewOnlyMode ? undefined : (e) => openEditCharge(idx, e)}
                title={viewOnlyMode ? undefined : "Editar cargo"}
              >
                {text}
                {!viewOnlyMode && (
                <button
                  type="button"
                  onClick={(event) => {
                    event.stopPropagation();
                    setActiveCharges((prevList) => {
                      const arr = [...prevList];
                      arr.splice(idx, 1);
                      return arr;
                    });
                      setSaleChargesTouched(true);
                  }}
                  className="rounded-full hover:bg-gray-200 dark:hover:bg-gray-600 p-0.5"
                  aria-label="Eliminar cargo"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
                )}
              </span>
            );
          })}
          {activeCharges.length === 0 && !showFeesEditor && !viewOnlyMode && (
            <button
              type="button"
              className="text-xs text-blue-600 dark:text-blue-300 underline"
              onClick={openAddChargeEditor}
            >
              {emptyCta}
            </button>
          )}
        </div>
        {activeCharges.length > 0 && !showFeesEditor && !viewOnlyMode && (
          <button
            type="button"
            className="inline-flex items-center gap-1 rounded-full border border-dashed border-blue-300 px-2 py-0.5 text-[11px] font-medium text-blue-600 hover:border-blue-400 hover:text-blue-700 dark:border-blue-300/40 dark:text-blue-200 dark:hover:border-blue-300"
            onClick={openAddChargeEditor}
          >
            <Plus className="h-3 w-3" /> {addButtonLabel}
          </button>
        )}
        {editingChargeIndex != null && (
          <div
            ref={popoverRef}
            style={{ position: 'fixed', top: popoverPos.top, left: popoverPos.left, zIndex: 60, minWidth: 280 }}
            className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-3"
            onKeyDown={handlePopoverKeyDown}
            onBlur={handlePopoverBlur}
            tabIndex={-1}
          >
            <div className="grid grid-cols-5 gap-2 items-end">
              <div className="col-span-2">
                <label className="block text-xs text-gray-600 dark:text-gray-300">Descripción</label>
                <select
                  value={editChargeDraft.kind}
                  onChange={(e) => setEditChargeDraft((prev) => ({ ...prev, kind: e.target.value, label: e.target.value === 'internal' ? 'Comisión interna' : 'Comisión externa' }))}
                  className="w-full border p-2 rounded border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  autoFocus
                >
                  <option value="external">Comisión externa</option>
                  <option value="internal">Comisión interna</option>
                </select>
              </div>
              <div>
                <label className="block text-xs text-gray-600 dark:text-gray-300">Valor</label>
                <input
                  type="number"
                  step="0.01"
                  value={editChargeDraft.value}
                  onChange={(e) => setEditChargeDraft((prev) => ({ ...prev, value: e.target.value }))}
                  className="w-full border p-2 rounded border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-600 dark:text-gray-300">Tipo</label>
                <select
                  value={editChargeDraft.type}
                  onChange={(e) => setEditChargeDraft((prev) => ({ ...prev, type: e.target.value }))}
                  className="w-full border p-2 rounded border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="flat">Monto</option>
                  <option value="percent">%</option>
                </select>
              </div>
              <div className="text-right flex gap-2 justify-end">
                <button
                  type="button"
                  className="inline-flex items-center gap-2 rounded-full border border-gray-300 px-3 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
                  onClick={closeEditCharge}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  className="inline-flex items-center gap-2 rounded-full bg-blue-600 px-3 py-1 text-xs font-semibold text-white hover:bg-blue-700"
                  onClick={saveEditChargeIfValid}
                >
                  Guardar
                </button>
              </div>
            </div>
            <p className="mt-2 text-[11px] text-gray-500 dark:text-gray-400">Enter para guardar · Esc para cerrar</p>
          </div>
        )}
        {showFeesEditor && (
          <div className="grid grid-cols-5 gap-2 items-end" onKeyDown={handleFeesKeyDown}>
            <div className="col-span-2">
              <label className="block text-xs text-gray-600 dark:text-gray-300">Descripción</label>
              <select
                value={draftCharge.kind}
                onChange={(e) => setDraftCharge((prev) => ({ ...prev, kind: e.target.value, label: e.target.value === 'internal' ? 'Comisión interna' : 'Comisión externa' }))}
                onBlur={handleFeesBlur}
                className="w-full border p-2 rounded border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value="external">Comisión externa</option>
                <option value="internal">Comisión interna</option>
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-600 dark:text-gray-300">Valor</label>
              <input
                type="number"
                step="0.01"
                value={draftCharge.value}
                onChange={(e) => setDraftCharge((prev) => ({ ...prev, value: e.target.value }))}
                onBlur={handleFeesBlur}
                className="w-full border p-2 rounded border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                placeholder="0.00"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-600 dark:text-gray-300">Tipo</label>
              <select
                value={draftCharge.type}
                onChange={(e) => setDraftCharge((prev) => ({ ...prev, type: e.target.value }))}
                onBlur={handleFeesBlur}
                className="w-full border p-2 rounded border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value="flat">Monto</option>
                <option value="percent">%</option>
              </select>
            </div>
            <div className="text-right">
              <button
                type="button"
                onClick={addDraftChargeIfValid}
                className="inline-flex items-center gap-2 rounded-full border border-gray-300 px-3 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
              >
                Agregar
              </button>
            </div>
            <div className="col-span-5">
              <button type="button" className="text-xs text-gray-500 dark:text-gray-400 underline" onClick={() => setShowFeesEditor(false)}>Ocultar</button>
            </div>
          </div>
        )}
        {!viewOnlyMode && <p className="text-[11px] text-gray-500 dark:text-gray-400">{tipText}</p>}
      </div>
    );
  };

  const [addDepositModal, setAddDepositModal] = useState(() => createEmptyAddDepositModal());
  const [depositHistoryModal, setDepositHistoryModal] = useState(() => createEmptyDepositHistoryModal());
  const [deliveryModal, setDeliveryModal] = useState(() => createEmptyDeliveryModal());
  const [proofFile, setProofFile] = useState(null);
  const [deliveryConfirmModal, setDeliveryConfirmModal] = useState(() => createEmptyDeliveryConfirmModal());
  const [confirmDialog, setConfirmDialog] = useState({ open: false, title: '', description: '', confirmLabel: 'Confirmar', intent: 'danger', destructive: true, onConfirm: null });
  const [page, setPage] = useState(1);
  const pageSize = 20;
  const { accountBalances = [], currentUser, branchBalances = [], transactionProfits = [] } = (function(){ try { return useData() || {}; } catch { return {}; } })();
  const accountOptions = useMemo(() => {
    const names = new Set();
    (accountBalances || []).forEach(a => a?.account_name && names.add(a.account_name));
    suppliers.forEach(s => s?.accountName && names.add(s.accountName));
    transactions.forEach(t => t?.depositAccount && names.add(t.depositAccount));
    return Array.from(names).sort((a,b)=>a.localeCompare(b));
  }, [accountBalances, suppliers, transactions]);

  // Toggle para mostrar/ocultar campos de broker
  const [useBroker, setUseBroker] = useState(false);

  const buildInitialTransaction = useCallback(() => ({
    type: 'sale',
    amount: '',
    clientId: '',
    supplierId: '',
    brokerId: '',
    brokerPercentage: '',
    branchId: currentUser?.branchId || '',
    sourceBranchId: currentUser?.branchId || '',
    targetBranchId: '',
    depositAccount: 'Firsbel',
    depositAmount: '',
    costAmount: '',
    purchasePercent: '',
    orderCharges: [],
    saleCharges: [],
  }), [currentUser?.branchId]);

  const resetNewTransactionState = useCallback(() => {
    setNewTransaction(buildInitialTransaction());
    setDraftCharge(() => createDefaultDraftCharge());
    setEditChargeDraft(() => createDefaultDraftCharge());
    setSaleChargesTouched(false);
    setShowFeesEditor(false);
    setEditingChargeIndex(null);
    setPopoverPos({ top: 0, left: 0 });
    setUseBroker(false);
    setClientSearchTerm('');
    setShowClientDropdown(false);
  }, [buildInitialTransaction]);

  // Movimientos de caja de sucursal eliminados de esta vista

  // (Removed) Firsbel balance UI and calculations

  // Eliminamos el reset automático al abrir el modal; lo haremos explícito en "Nueva operación"

  useEffect(() => {
    const handler = () => handleStartCreate();
    window.addEventListener('open-new-transaction', handler);
    return () => window.removeEventListener('open-new-transaction', handler);
  }, []);

  useEffect(() => {
    setShowFeesEditor(false);
    setEditingChargeIndex(null);
    setDraftCharge((prev) => {
      const isSale = newTransaction.type === 'sale';
      const kind = isSale ? 'internal' : 'external';
      return {
        ...prev,
        kind,
        label: kind === 'internal' ? 'Comisión interna' : 'Comisión externa',
        type: isSale ? 'percent' : 'flat',
        value: '',
      };
    });
  }, [newTransaction.type]);

  // Close client dropdown when clicking outside
  useEffect(() => {
    if (!showClientDropdown) return;
    const handleClickOutside = (e) => {
      if (!e.target.closest('.client-search-container')) {
        setShowClientDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showClientDropdown]);

  const resetModals = useCallback(() => {
    setAddDepositModal(createEmptyAddDepositModal());
    setDepositHistoryModal(createEmptyDepositHistoryModal());
    setDeliveryModal(createEmptyDeliveryModal());
    setDeliveryConfirmModal(createEmptyDeliveryConfirmModal());
    setProofFile(null);
  }, []);

  const openConfirmDialog = ({ title, description, confirmLabel = 'Confirmar', intent = 'danger', destructive = intent === 'danger', onConfirm }) => {
    setConfirmDialog({
      open: true,
      title: title || '¿Estás seguro?',
      description: description || 'Esta acción no se puede deshacer.',
      confirmLabel,
      intent,
      destructive,
      onConfirm,
    });
  };

  const closeConfirmDialog = () => {
    setConfirmDialog((prev) => ({ ...prev, open: false, onConfirm: null }));
  };

  const closePrimaryModal = useCallback(() => {
    setShowModal(false);
    setWizardStep(1);
    setEditingTransactionId(null);
    setIsEditing(false);
    setViewOnlyMode(false);
    resetNewTransactionState();
  }, [resetNewTransactionState]);

  // Helpers to present user-friendly status labels and styles
  // For delivered: show "Recibido" for orders, "Entregado" for sales
  const getStatusLabel = (s, type) => {
    if (!s) return '—';
    if (s === 'pending_deposit') return 'Pendiente depósito';
    if (s === 'pending_delivery') return type === 'sale' ? 'Pendiente entrega' : 'Pendiente recepción';
    if (s === 'delivered') {
      if (type === 'order') return 'Recibido';
      if (type === 'transfer') return 'Completado';
      return 'Entregado';
    }
    if (s === 'cancelled') return 'Cancelado';
    if (s === 'pending') return 'Pendiente';
    return s;
  };

  const getStatusClass = (s) => {
    if (s === 'delivered') return 'bg-green-200 text-green-800';
    if (s === 'pending_deposit' || s === 'pending') return 'bg-yellow-100 text-yellow-800';
    if (s === 'pending_delivery') return 'bg-blue-100 text-blue-800';
    if (s === 'cancelled') return 'bg-red-100 text-red-800';
    return 'bg-gray-100 text-gray-800';
  };

  const handleInputChange = (e) => {
    const { name } = e.target;
    const rawValue = e.target.value;
    const normalizedValue = typeof rawValue === 'string' ? rawValue : String(rawValue ?? '');

    // When switching type, clear or adapt fields per operation
    if (name === 'type') {
      setNewTransaction((prev) => {
        const next = { ...prev, type: normalizedValue };
        if (normalizedValue === 'sale') {
          next.supplierId = '';
          next.orderCharges = [];
          next.saleCharges = prev.saleCharges && Array.isArray(prev.saleCharges) ? [...prev.saleCharges] : [];
          next.targetBranchId = '';
          next.sourceBranchId = prev.branchId || currentUser?.branchId || '';
          if (!next.depositAccount) next.depositAccount = 'Firsbel';
        } else if (normalizedValue === 'order') {
          next.clientId = '';
          next.brokerId = '';
          next.brokerPercentage = '';
          next.brokerCommission = undefined;
          next.saleCharges = [];
          next.targetBranchId = '';
          next.sourceBranchId = prev.branchId || currentUser?.branchId || '';
        } else if (normalizedValue === 'transfer') {
          next.clientId = '';
          next.supplierId = '';
          next.brokerId = '';
          next.brokerPercentage = '';
          next.brokerCommission = undefined;
          next.saleCharges = [];
          next.depositAccount = '';
          next.targetBranchId = '';
          next.sourceBranchId = prev.branchId || currentUser?.branchId || '';
        }
        return next;
      });
      if (normalizedValue !== 'sale') {
        setUseBroker(false);
      }
      return;
    }

    // Normalize ID comparisons to strings (DB uses UUIDs)
    if (name === 'clientId') {
      const client = clients.find((c) => String(c.id) === String(normalizedValue));
      if (client) {
        setNewTransaction((prev) => {
          const next = { ...prev, clientId: normalizedValue };
          // Reset touched state when client changes
          setSaleChargesTouched(false);
          if (client.brokerId) {
            const assignedBroker = brokers.find((b) => String(b.id) === String(client.brokerId));
            if (assignedBroker) {
              next.brokerId = String(assignedBroker.id);
              next.brokerPercentage = assignedBroker.commissionRate != null ? Number(assignedBroker.commissionRate) : '';
              if ((next.type || '') === 'sale') setUseBroker(true);
            }
          }
          if ((next.type || '') === 'sale') {
            const shouldHydrateCharges = (!Array.isArray(next.saleCharges) || next.saleCharges.length === 0) && !saleChargesTouched;
            if (shouldHydrateCharges) {
              const defaults = ensureClientDefaultSaleCharges(client);
              if (defaults.length > 0) {
                next.saleCharges = defaults;
              }
            }
            const amount = Number(next.amount || 0);
            const pct = Number(next.brokerPercentage || 0);
            if (amount > 0 && pct > 0) {
              next.brokerCommission = Math.round((amount * pct / 100) * 100) / 100;
            }
          }
          return next;
        });
        return;
      }
    }

    if (name === 'supplierId') {
      const supplier = suppliers.find((s) => String(s.id) === String(normalizedValue));
      if (supplier) {
        setNewTransaction((prev) => {
          const next = { ...prev, supplierId: normalizedValue };
          if (supplier.accountName) next.depositAccount = supplier.accountName;
          // On provider change for orders, remove previous charges and hydrate from new defaults
          if (next.type === 'order') {
            next.orderCharges = [];
            if (Array.isArray(supplier.defaultOrderCharges) && supplier.defaultOrderCharges.length > 0) {
            next.orderCharges = supplier.defaultOrderCharges.map((c, i) => ({
              id: `${Date.now()}-${i}`,
              type: c.type || 'flat',
              value: c.value || 0,
              mode: c.mode || 'on_top',
              kind: c.kind || (String(c.label || '').toLowerCase().includes('interna') ? 'internal' : 'external'),
              label: c.label || ((c.kind === 'internal') ? 'Comisión interna' : 'Comisión externa'),
            }));
            }
          }
          return next;
        });
        return;
      }
    }

    if (name === 'brokerId') {
      const broker = brokers.find((b) => String(b.id) === String(normalizedValue));
      setNewTransaction((prev) => {
        const next = { ...prev, brokerId: normalizedValue };
        if (broker && broker.commissionRate != null) {
          next.brokerPercentage = Number(broker.commissionRate);
        }
        if ((next.type || '') === 'sale') {
          const amount = Number(next.amount || 0);
          const pct = Number(next.brokerPercentage || 0);
          if (amount > 0 && pct > 0) {
            next.brokerCommission = Math.round((amount * pct / 100) * 100) / 100;
          }
        }
        return next;
      });
      return;
    }

    setNewTransaction((prev) => {
      const next = { ...prev, [name]: normalizedValue };
      if (name === 'branchId' && (next.type || '') === 'transfer') {
        next.sourceBranchId = normalizedValue;
        if (String(normalizedValue || '') === String(next.targetBranchId || '')) {
          next.targetBranchId = '';
        }
      }
      if (name === 'type') {
        // Reset touched state when changing type
        setSaleChargesTouched(false);
      }
      if (name === 'targetBranchId' && (next.type || '') === 'transfer') {
        next.targetBranchId = normalizedValue;
      }

      const isSaleType = (next.type || '') === 'sale';
      const amount = Number(name === 'amount' ? normalizedValue : next.amount || 0);
      const pct = Number(name === 'brokerPercentage' ? normalizedValue : next.brokerPercentage || 0);
      if (isSaleType && amount > 0 && pct > 0) {
        next.brokerCommission = Math.round((amount * pct / 100) * 100) / 100;
      } else if (isSaleType && name === 'brokerPercentage' && (normalizedValue === '' || Number(normalizedValue) === 0)) {
        next.brokerCommission = undefined;
      }
      return next;
    });
  };

  // Helpers for fees (charges) chips
  const addDraftChargeIfValid = () => {
    const rawVal = draftCharge.value;
    const valNum = Number(rawVal);
    if (!rawVal && rawVal !== 0) return; // empty
    if (Number.isNaN(valNum) || valNum <= 0) return; // require positive value
    const kind = draftCharge.kind || 'external';
    const label = kind === 'internal' ? 'Comisión interna' : 'Comisión externa';
    setActiveCharges((prevList) => ([
      ...prevList,
      {
        id: buildChargeId(),
        type: draftCharge.type || 'flat',
        value: valNum,
        mode: 'on_top',
        kind,
        label,
      },
    ]));
    setSaleChargesTouched(true);
    setDraftCharge({ kind, label, value: '', type: draftCharge.type || 'flat' });
  };

  const handleFeesKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addDraftChargeIfValid();
    }
  };

  const handleFeesBlur = (e) => {
    // Commit draft only when focus leaves the entire fees module (container)
    const next = e.relatedTarget;
    const container = feesContainerRef.current;
    if (!container) return;
    if (next && container.contains(next)) return; // still inside module
    addDraftChargeIfValid();
  };

  const openEditCharge = (idx, event) => {
    const ch = getActiveCharges()[idx];
    if (!ch) return;
    // Position near the clicked chip
    const el = event.currentTarget;
    const rect = el.getBoundingClientRect();
    const top = Math.round(rect.bottom + 6 + window.scrollY);
    const left = Math.round(rect.left + window.scrollX);
    setPopoverPos({ top, left });
    setEditingChargeIndex(idx);
    setEditChargeDraft({
      kind: ch.kind || (String(ch.label || '').toLowerCase().includes('interna') ? 'internal' : 'external'),
      label: ch.label || (String(ch.kind) === 'internal' ? 'Comisión interna' : 'Comisión externa'),
      value: ch.value != null ? String(ch.value) : '',
      type: ch.type || 'flat',
    });
    // Ensure any pending draft new charge is committed first
    addDraftChargeIfValid();
  };

  const closeEditCharge = () => {
    setEditingChargeIndex(null);
  };

  const saveEditChargeIfValid = () => {
    const idx = editingChargeIndex;
    if (idx == null) return;
    const valNum = Number(editChargeDraft.value);
    if (Number.isNaN(valNum) || valNum <= 0) {
      // Invalid -> just close without saving
      closeEditCharge();
      return;
    }
    setActiveCharges((prevList) => {
      const arr = [...prevList];
      const current = arr[idx];
      if (!current) return prevList;
      const kind = editChargeDraft.kind || 'external';
      const label = kind === 'internal' ? 'Comisión interna' : 'Comisión externa';
      arr[idx] = {
        ...current,
        label,
        value: valNum,
        type: editChargeDraft.type || 'flat',
        mode: 'on_top',
        kind,
      };
      return arr;
    });
    setSaleChargesTouched(true);
    closeEditCharge();
  };

  const handlePopoverKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      saveEditChargeIfValid();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      closeEditCharge();
    }
  };

  const handlePopoverBlur = (e) => {
    const next = e.relatedTarget;
    const container = popoverRef.current;
    if (!container) return;
    if (next && container.contains(next)) return; // still inside the popover
    // Commit edit (if valid) when leaving popover
    saveEditChargeIfValid();
  };

  useEffect(() => {
    const handleGlobalEscape = (event) => {
      if (event.key !== 'Escape') return;

      if (editingChargeIndex != null) {
        event.preventDefault();
        setEditingChargeIndex(null);
        return;
      }

      if (showFeesEditor) {
        event.preventDefault();
        setShowFeesEditor(false);
        return;
      }

      if (deliveryConfirmModal.show) {
        event.preventDefault();
        setDeliveryConfirmModal(createEmptyDeliveryConfirmModal());
        return;
      }

      if (deliveryModal.show) {
        event.preventDefault();
        setDeliveryModal(createEmptyDeliveryModal());
        setProofFile(null);
        return;
      }

      if (depositHistoryModal.show) {
        event.preventDefault();
        setDepositHistoryModal(createEmptyDepositHistoryModal());
        return;
      }

      if (addDepositModal.show) {
        event.preventDefault();
        setAddDepositModal(createEmptyAddDepositModal());
        return;
      }

      if (showModal) {
        event.preventDefault();
        closePrimaryModal();
      }
    };

    window.addEventListener('keydown', handleGlobalEscape);
    return () => window.removeEventListener('keydown', handleGlobalEscape);
  }, [
    showModal,
    showFeesEditor,
    editingChargeIndex,
    addDepositModal.show,
    depositHistoryModal.show,
    deliveryModal.show,
    deliveryConfirmModal.show,
    closePrimaryModal,
  ]);

  const handleStartCreate = () => {
    setEditingTransactionId(null);
    setIsEditing(false);
    setViewOnlyMode(false);
    setWizardStep(1);
    resetNewTransactionState();
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (savingTx) return;
    // Client-side validation aligned with server rules for friendlier UX
    if (Array.isArray(validationErrors) && validationErrors.length > 0) {
      try { push(validationErrors[0], { type: 'error' }); } catch {}
      return;
    }
    setSavingTx(true);
    try {
      const baseTx = isEditing ? (transactions.find(t => String(t.id) === String(editingTransactionId)) || {}) : {};
      const tx = {
        ...baseTx,
        ...newTransaction,
        id: isEditing ? editingTransactionId : (newTransaction.id || Date.now()),
        date: baseTx.date || new Date().toISOString().split('T')[0],
        deposits: editingTransactionId ? (baseTx.deposits || []) : [],
      };
      // Mantener cargos tal cual; modo por cargo será ignorado en cálculos (si existe)
      if (Array.isArray(newTransaction.orderCharges)) {
        tx.orderCharges = newTransaction.orderCharges.map(c => ({ ...c }));
      }
      if (Array.isArray(newTransaction.saleCharges)) {
        tx.saleCharges = newTransaction.saleCharges.map(c => ({ ...c }));
      }
      if (tx.amount) tx.amount = Number(tx.amount);
      if (tx.depositAmount) tx.depositAmount = Number(tx.depositAmount);

      if (isEditing) {
        await onEditTransaction(tx);
      } else {
        await onAddTransaction(tx);
      }
      // Only close modal and reset if successful
      closePrimaryModal();
      resetModals();
    } catch (err) {
      console.error('handleSubmit transaction', err);
      // Error notification is handled by App.js
      // Don't close modal so user can see the error and try again
    } finally {
      setSavingTx(false);
    }
  };

  const openEdit = (t) => {
    setEditingTransactionId(t.id);
    setIsEditing(true);
    setViewOnlyMode(false);
    setWizardStep(1);
    const typeValue = t.type || t.transaction_type;
    const prepared = {
      ...t,
      sourceBranchId: t.sourceBranchId ?? t.source_branch_id ?? t.branchId ?? t.branch_id ?? '',
      targetBranchId: t.targetBranchId ?? t.target_branch_id ?? t.metadata?.transfer?.targetBranchId ?? t.metadata?.targetBranchId ?? '',
    };
    if (typeValue === 'sale') {
      const charges = ensureSaleChargesFrom(prepared);
      prepared.saleCharges = charges;
    }
    setNewTransaction(prepared);
    // Mostrar/ocultar sección de broker según datos existentes
    setUseBroker((t.type || t.transaction_type) === 'sale' && Boolean(t.brokerId || t.broker_id));
    setShowModal(true);
  };

  const openView = (t) => {
    setEditingTransactionId(t.id);
    setIsEditing(false);
    setViewOnlyMode(true);
    setWizardStep(1);
    const typeValue = t.type || t.transaction_type;
    const prepared = {
      ...t,
      sourceBranchId: t.sourceBranchId ?? t.source_branch_id ?? t.branchId ?? t.branch_id ?? '',
      targetBranchId: t.targetBranchId ?? t.target_branch_id ?? t.metadata?.transfer?.targetBranchId ?? t.metadata?.targetBranchId ?? '',
    };
    if (typeValue === 'sale') {
      const charges = ensureSaleChargesFrom(prepared);
      prepared.saleCharges = charges;
    }
    setNewTransaction(prepared);
    // Mostrar/ocultar sección de broker según datos existentes
    setUseBroker((t.type || t.transaction_type) === 'sale' && Boolean(t.brokerId || t.broker_id));
    setShowModal(true);
  };

  const handleDeleteTransactionClick = (tx) => {
    openConfirmDialog({
      title: 'Eliminar operación',
      description: 'Esta acción eliminará definitivamente la operación y sus depósitos relacionados.',
      confirmLabel: 'Eliminar',
      onConfirm: async () => {
        try {
          await onDeleteTransaction(tx.id);
        } catch (err) {
          console.error('handleDeleteTransactionClick', err);
        } finally {
          closeConfirmDialog();
        }
      },
    });
  };

  const handleToggleCommissionStatus = async (tx) => {
    const paid = Boolean(tx.brokerCommissionPaid ?? tx.broker_commission_paid);
    try {
      await onToggleCommission(tx.id, !paid);
    } catch (err) {
      console.error('handleToggleCommissionStatus', err);
    }
  };

  const openAddDeposit = (transactionId) => setAddDepositModal({ show: true, transactionId, amount: '', date: '', reference: '', proofFile: null });

  const handleAddDeposit = () => {
    const tx = transactions.find(t => t.id === addDepositModal.transactionId);
    if (!tx) return;
  // Do NOT pre-assign an id; absence of id signals the backend to insert and return a real id
  const newDeposit = { date: addDepositModal.date, amount: Number(addDepositModal.amount || 0), reference: addDepositModal.reference || '', proofFile: addDepositModal.proofFile || null, usedAmount: 0 };
    const newDeposits = [...(tx.deposits || []), newDeposit];
    onUpdateTransactionDeposits(tx.id, newDeposits);
    const supplier = suppliers.find(s => s.id === tx.supplierId);
    if (supplier && supplier.accountName && tx.depositAccount !== supplier.accountName) {
      onEditTransaction({ ...tx, depositAccount: supplier.accountName, depositAmount: newDeposits.reduce((s,d)=>s+Number(d.amount||0),0) });
    }
    push('Depósito agregado', { type: 'success' });
    setAddDepositModal({ show: false, transactionId: null, amount: '', date: '', reference: '', proofFile: null });
  };

  const handleDeleteDepositLocal = (tx, depositId) => {
    openConfirmDialog({
      title: 'Eliminar depósito',
      description: 'Se eliminará el comprobante seleccionado de esta operación.',
      confirmLabel: 'Eliminar depósito',
      onConfirm: () => {
        const newDeposits = (tx.deposits || []).filter(d => d.id !== depositId);
        onUpdateTransactionDeposits(tx.id, newDeposits);
        setDepositHistoryModal({ show: true, transaction: { ...tx, deposits: newDeposits } });
        closeConfirmDialog();
      },
    });
  };

  const openDepositHistory = (tx) => setDepositHistoryModal({ show: true, transaction: tx });

  const prepareDelivery = (tx, amount = '') => {
    // For sales: deliver base minus internal commission (we keep internal as profit)
    // For orders: branch cash impact is the base amount only
    const isSale = tx.type === 'sale';
    const isOrder = tx.type === 'order';
    const base = tx.amount != null ? Number(tx.amount) : 0;
    
    let expected = base;
    
    if (isSale) {
      const saleBreakdown = computeSaleChargeBreakdown(tx, { baseAmount: base });
      const internal = Number(saleBreakdown?.internal || 0) || 0;
      expected = Math.max(0, base - internal);
    } else if (isOrder) {
      // For orders, use cashNeeded which now equals base (fees not on branch cash)
      const orderBreakdown = computeOrderChargeBreakdown(tx, { baseAmount: base });
      expected = orderBreakdown.cashNeeded;
    }
    
    setDeliveryModal({ show: true, transactionId: tx.id, amount: amount || expected });
    setProofFile(null);
  };


  const attemptConfirmDelivery = () => {
    const received = Number(deliveryModal.amount || 0);
    const tx = transactions.find(t => t.id === deliveryModal.transactionId) || {};
    const isSale = tx.type === 'sale';
    const isOrder = tx.type === 'order';
    const base = tx.amount != null ? Number(tx.amount) : 0;
    
    let expected = base;
    
    if (isSale) {
      const saleBreakdown = computeSaleChargeBreakdown(tx, { baseAmount: base });
      const internal = Number(saleBreakdown?.internal || 0) || 0;
      expected = Math.max(0, base - internal);
    } else if (isOrder) {
      // For orders, use cashNeeded which now equals base (fees not on branch cash)
      const orderBreakdown = computeOrderChargeBreakdown(tx, { baseAmount: base });
      expected = orderBreakdown.cashNeeded;
    }
    
    if (Math.abs(received - expected) > 0) {
      setDeliveryConfirmModal({ show: true, expected, received });
    } else {
      handleConfirmDelivery();
    }
  };

  const handleConfirmDelivery = () => {
    const amount = Number(deliveryModal.amount || 0);
    onMarkAsDelivered(deliveryModal.transactionId, amount, proofFile);
    const tx = transactions.find(t => t.id === deliveryModal.transactionId);
    const typeValue = tx?.type || tx?.transaction_type;
    const toastMessage = typeValue === 'sale'
      ? 'Entrega registrada'
      : typeValue === 'transfer'
        ? 'Transferencia registrada'
        : 'Recepción registrada';
    push(toastMessage, { type: 'success' });
    resetModals();
  };

  const handleCancelOrderLocal = (transactionId) => {
    openConfirmDialog({
      title: 'Cancelar operación',
      description: 'La operación pasará al estado cancelado y no podrá revertirse desde la interfaz.',
      confirmLabel: 'Cancelar operación',
      onConfirm: async () => {
        try {
          await onCancelOrder(transactionId);
          push('Operación cancelada', { type: 'warning' });
        } catch (err) {
          console.error('handleCancelOrderLocal', err);
        } finally {
          closeConfirmDialog();
        }
      },
    });
  };

  const clientMap = useMemo(() => {
    const map = {};
    clients.forEach((c) => {
      map[String(c.id)] = c.name || c.companyName || c.legalName || c.displayName || c.contactName || '';
    });
    return map;
  }, [clients]);

  const supplierMap = useMemo(() => {
    const map = {};
    suppliers.forEach((s) => {
      map[String(s.id)] = s.name || s.companyName || s.legalName || '';
    });
    return map;
  }, [suppliers]);

  const brokerMap = useMemo(() => {
    const map = {};
    brokers.forEach((b) => {
      map[String(b.id)] = b.name || b.displayName || b.fullName || '';
    });
    return map;
  }, [brokers]);

  const branchMap = useMemo(() => {
    const map = {};
    (branches || []).forEach((b) => { map[String(b.id)] = b.name || ''; });
    return map;
  }, [branches]);

  // Helpers for date handling
  const toStartOfDay = (d) => { const nd = new Date(d); nd.setHours(0,0,0,0); return nd; };
  const toEndOfDay = (d) => { const nd = new Date(d); nd.setHours(23,59,59,999); return nd; };
  const parseTxDate = (tx) => {
    const raw = tx?.date || tx?.created_at;
    const d = raw instanceof Date ? raw : new Date(raw);
    return Number.isNaN(d?.getTime?.()) ? null : d;
  };

  // Exports
  const buildOperationExportRows = (rows = filteredTransactions) => {
    const getClient = (tx) => clientMap[String(tx.clientId ?? tx.client_id)] || '';
    const getSupplier = (tx) => supplierMap[String(tx.supplierId ?? tx.supplier_id)] || '';
    const getBranchName = (tx) => {
      const sId = String(tx.sourceBranchId ?? tx.source_branch_id ?? tx.branchId ?? tx.branch_id ?? '');
      const tId = String(tx.targetBranchId ?? tx.target_branch_id ?? '');
      if ((tx.type || tx.transaction_type) === 'transfer') {
        const s = branchMap[sId] || 'Origen';
        const t = branchMap[tId] || 'Destino';
        return `${s} -> ${t}`;
      }
      return branchMap[sId] || '';
    };
    return rows.map((tx) => ({
      id: tx.id,
      fecha: tx.date || tx.created_at,
      tipo: tx.type || tx.transaction_type,
      cliente: getClient(tx),
      proveedor: getSupplier(tx),
      sucursal: getBranchName(tx),
      estado: tx.status,
      monto: Number(tx.amount || 0),
      cuenta_deposito: tx.depositAccount || '',
      comision_broker: Number(tx.brokerCommission ?? tx.broker_commission ?? 0) || 0,
      comision_broker_pagada: Boolean(tx.brokerCommissionPaid ?? tx.broker_commission_paid) ? 'sí' : 'no',
    }));
  };
  const handleExportCSV = () => {
    const rows = buildOperationExportRows();
    exportToCSV(`operaciones_${new Date().toISOString().slice(0,10)}`, rows);
    setShowExportMenu(false);
  };
  const handleExportXLSX = async () => {
    const rows = buildOperationExportRows();
    await exportToXLSX(`operaciones_${new Date().toISOString().slice(0,10)}`, [
      { name: 'Operaciones', data: rows },
    ]);
    setShowExportMenu(false);
  };

  const now = useMemo(() => new Date(), []);
  const { rangeStart, rangeEnd } = useMemo(() => {
    if (dateRangeType === 'today') {
      const d = new Date();
      return { rangeStart: toStartOfDay(d), rangeEnd: toEndOfDay(d) };
    }
    const map = { '7d': 7, '14d': 14, '30d': 30, '90d': 90 };
    if (map[dateRangeType]) {
      const end = toEndOfDay(new Date());
      const start = new Date(end);
      start.setDate(start.getDate() - map[dateRangeType]);
      return { rangeStart: start, rangeEnd: end };
    }
    if (dateRangeType === 'custom' && customStart && customEnd) {
      return { rangeStart: toStartOfDay(new Date(customStart)), rangeEnd: toEndOfDay(new Date(customEnd)) };
    }
    return { rangeStart: null, rangeEnd: null };
  }, [dateRangeType, customStart, customEnd]);

  const filteredTransactions = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    const status = statusFilter === 'all' ? null : statusFilter;
    const type = typeFilter === 'all' ? null : typeFilter;

    const result = transactions.filter((tx) => {
      // Date range filter first
      if (rangeStart && rangeEnd) {
        const d = parseTxDate(tx);
        if (!d || d < rangeStart || d > rangeEnd) return false;
      }
      if (type && tx.type !== type) return false;
      const currentStatus = tx.status || 'pending';
      // By default (statusFilter=all), hide cancelled to reflect delete semantics
      if (!status && currentStatus === 'cancelled') return false;
      if (status && currentStatus !== status) return false;
      // Branch filter: match source/branchId or target for transfers
      if (selectedBranchIds.length > 0) {
        const bId = String(tx.branchId ?? tx.branch_id ?? tx.sourceBranchId ?? tx.source_branch_id ?? '');
        const tId = String(tx.targetBranchId ?? tx.target_branch_id ?? '');
        const ok = selectedBranchIds.includes(bId) || (tId && selectedBranchIds.includes(tId));
        if (!ok) return false;
      }
      if (selectedClientIds.length > 0) {
        const id = String(tx.clientId ?? tx.client_id ?? '');
        if (!selectedClientIds.includes(id)) return false;
      }
      if (selectedSupplierIds.length > 0) {
        const id = String(tx.supplierId ?? tx.supplier_id ?? '');
        if (!selectedSupplierIds.includes(id)) return false;
      }
      if (selectedBrokerIds.length > 0) {
        const id = String(tx.brokerId ?? tx.broker_id ?? '');
        if (!selectedBrokerIds.includes(id)) return false;
      }
      if (commissionPaidFilter !== 'all') {
        const hasCommission = Number(tx.brokerCommission ?? tx.broker_commission ?? 0) > 0;
        const paid = Boolean(tx.brokerCommissionPaid ?? tx.broker_commission_paid);
        if (!hasCommission) return false;
        if (commissionPaidFilter === 'paid' && !paid) return false;
        if (commissionPaidFilter === 'pending' && paid) return false;
      }
      // Amount range
      const amt = Number(tx.amount || 0) || 0;
      if (amountMin && amt < Number(amountMin)) return false;
      if (amountMax && amt > Number(amountMax)) return false;
      // Deposit account
      if (depositAccountFilter) {
        const acc = (tx.depositAccount || '').toString().toLowerCase();
        if (acc !== depositAccountFilter.toLowerCase()) return false;
      }
      if (!query) return true;
      const haystack = [
        tx.description,
        tx.depositAccount,
        clientMap[String(tx.clientId ?? tx.client_id)],
        supplierMap[String(tx.supplierId ?? tx.supplier_id)],
        brokerMap[String(tx.brokerId ?? tx.broker_id)],
        tx.id,
      ]
        .filter(Boolean)
        .map((value) => value.toString().toLowerCase());
      return haystack.some((value) => value.includes(query));
    });

    const direction = sortDir === 'asc' ? 1 : -1;
    const statusOrder = {
      cancelled: 0,
      pending: 1,
      pending_deposit: 2,
      pending_delivery: 3,
      delivered: 4,
    };

    const getValue = (tx) => {
      if (sortKey === 'amount') return Number(tx.amount || 0);
      if (sortKey === 'deposit') {
        const depositsSum = Array.isArray(tx.deposits)
          ? tx.deposits.reduce((s, d) => s + (Number(d.amount || 0) || 0), 0)
          : Number(tx.depositAmount || 0);
        return Number(depositsSum || 0);
      }
      if (sortKey === 'status') return statusOrder[tx.status] ?? 1;
      // default to date
      return new Date(tx.date || tx.created_at || 0).getTime();
    };

    return result.sort((a, b) => {
      const av = getValue(a);
      const bv = getValue(b);
      if (av === bv) return 0;
      return av > bv ? direction : -direction;
    });
  }, [transactions, typeFilter, statusFilter, searchTerm, sortKey, sortDir, clientMap, supplierMap, brokerMap, rangeStart, rangeEnd]);

  const start = (page - 1) * pageSize;
  const pageRows = filteredTransactions.slice(start, start + pageSize);
  const selectedSet = new Set(selectedIds);
  const allPageSelected = pageRows.length > 0 && pageRows.every((tx) => selectedSet.has(String(tx.id)));

  const toggleSelectAll = () => {
    if (allPageSelected) {
      setSelectedIds((prev) => prev.filter((id) => !pageRows.some((tx) => String(tx.id) === id)));
    } else {
      const newSelections = pageRows
        .map((tx) => String(tx.id))
        .filter((id) => !selectedSet.has(id));
      setSelectedIds((prev) => [...prev, ...newSelections]);
    }
  };

  const toggleSelectTransaction = (id) => {
    const idStr = String(id);
    setSelectedIds((prev) => (prev.includes(idStr) ? prev.filter((value) => value !== idStr) : [...prev, idStr]));
  };

  const toggleRowExpansion = (id) => {
    setExpandedRows((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const selectedTransactions = useMemo(() => {
    return selectedIds
      .map((id) => transactions.find((tx) => String(tx.id) === id))
      .filter(Boolean);
  }, [selectedIds, transactions]);

  const handleBulkDelete = () => {
    if (!selectedIds.length) return;
    openConfirmDialog({
      title: `Eliminar ${selectedIds.length} operación${selectedIds.length > 1 ? 'es' : ''}`,
      description: 'Se eliminarán todas las operaciones seleccionadas y sus registros asociados.',
      confirmLabel: 'Eliminar seleccionadas',
      onConfirm: async () => {
        try {
          const ids = selectedTransactions.map((tx) => tx.id);
          await Promise.all(ids.map((id) => onDeleteTransaction(id)));
        } catch (err) {
          console.error('handleBulkDelete', err);
        } finally {
          setSelectedIds([]);
          closeConfirmDialog();
        }
      },
    });
  };

  const handleBulkCommissionToggle = (paid) => {
    const eligible = selectedTransactions.filter((tx) => tx.type === 'sale' && (tx.brokerCommission || tx.broker_commission));
    if (!eligible.length) return;
    openConfirmDialog({
      title: `${paid ? 'Marcar pagadas' : 'Marcar pendientes'} (${eligible.length})`,
      description: paid
        ? 'Las comisiones de los brokers seleccionados se marcarán como pagadas.'
        : 'Las comisiones se restaurarán al estado pendiente.',
      confirmLabel: paid ? 'Marcar como pagadas' : 'Marcar como pendientes',
  intent: 'info',
      destructive: false,
      onConfirm: async () => {
        try {
          await Promise.all(eligible.map((tx) => onToggleCommission(tx.id, paid)));
        } catch (err) {
          console.error('handleBulkCommissionToggle', err);
        } finally {
          closeConfirmDialog();
        }
      },
    });
  };

  const statusOptions = [
    { value: 'all', label: 'Todos los estados' },
    { value: 'pending', label: 'Pendiente' },
    { value: 'pending_deposit', label: 'Pendiente depósito' },
    { value: 'pending_delivery', label: 'Pendiente entrega' },
    { value: 'delivered', label: 'Entregado' },
    { value: 'cancelled', label: 'Cancelado' },
  ];

  const sortOptions = [
    { value: 'date-desc', label: 'Fecha (recientes)' },
    { value: 'date-asc', label: 'Fecha (antiguas)' },
    { value: 'amount-desc', label: 'Monto ↓' },
    { value: 'amount-asc', label: 'Monto ↑' },
    { value: 'deposit-desc', label: 'Depósitos ↓' },
    { value: 'deposit-asc', label: 'Depósitos ↑' },
    { value: 'status-asc', label: 'Estado A→Z' },
    { value: 'status-desc', label: 'Estado Z→A' },
  ];

  const handleSortChange = (value) => {
    const [key, direction] = value.split('-');
    setSortKey(key);
    setSortDir(direction === 'asc' ? 'asc' : 'desc');
  };

  const selectedCount = selectedIds.length;
  const eligibleCommissionCount = selectedTransactions.filter((tx) => tx.type === 'sale' && (tx.brokerCommission || tx.broker_commission)).length;
  const currentSortValue = `${sortKey}-${sortDir}`;
  const emptyDescription = searchTerm
    ? `No encontramos operaciones que coincidan con "${searchTerm}". Ajusta los filtros o crea una nueva operación.`
    : 'Registra tu primera operación para comenzar a controlar el flujo de efectivo.';

  useEffect(() => {
    setPage(1);
  }, [typeFilter, statusFilter, searchTerm, transactions.length]);

  useEffect(() => {
    setSelectedIds((prev) => prev.filter((id) => filteredTransactions.some((tx) => String(tx.id) === id)));
  }, [filteredTransactions]);

  return (
    <div className="min-h-screen bg-gray-50 px-4 py-6 dark:bg-gray-950 sm:px-6 lg:px-10">
      <div className="rounded-2xl border border-gray-200 bg-white p-6  dark:border-gray-800 dark:bg-gray-900 neon-border">
        <div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
          <div className="space-y-2">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-blue-600 dark:text-blue-400">Operaciones</p>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-50">Gestión de operaciones</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">Monitorea ventas, pedidos, depósitos y entregas en tiempo real.</p>
          </div>
          <div className="flex flex-col items-end gap-3 sm:flex-row sm:items-center sm:gap-6">
            <button
              onClick={handleStartCreate}
              className="inline-flex items-center gap-2 rounded-full bg-blue-600 px-4 py-2 text-sm font-semibold text-white  transition hover:bg-blue-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-500"
            >
              <Plus className="h-4 w-4" />
              Nueva operación
            </button>
          </div>
        </div>
      </div>

      <div className="mt-6 space-y-4">
        <div className="rounded-2xl border border-gray-200 bg-white p-4 dark:border-gray-800 dark:bg-gray-900 neon-border">
          <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
            <div className="flex flex-col gap-2 lg:flex-row lg:items-center lg:gap-3">
              <div className="relative w-full min-w-[200px] lg:w-64">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                <input
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                  type="search"
                  placeholder="Buscar por cliente, proveedor, referencia o ID…"
                  className="w-full rounded-full border border-gray-200 bg-gray-50 py-1.5 pl-10 pr-3 text-sm text-gray-700 transition focus:border-blue-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-200 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100 dark:focus:border-blue-500 dark:focus:ring-blue-400/40"
                />
              </div>
              {/* Resumen movido al centro en pantallas grandes */}
              {/* Type chips moved below toolbar */}
            </div>
            {/* Center: summary card */}
            <div className="hidden xl:flex xl:flex-[0_0_auto] items-stretch justify-center">
              <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3 bg-gray-50 dark:bg-gray-800/50 min-w-[320px]">
                {newTransaction.type === 'sale' ? (() => {
                  const base = Number(newTransaction.amount || 0) || 0;
                  const breakdown = computeSaleChargeBreakdown(newTransaction, { baseAmount: base });
                  const ourFee = Number(breakdown.internal || 0) || 0;
                  const externalCharges = Number(breakdown.external || 0) || 0;
                  const totalToCollect = Number(breakdown.totalToCollect || (base + externalCharges));
                  const totalToDeliver = Number(breakdown.totalToDeliver || Math.max(0, base - ourFee));
                  const brokerPct = Number(newTransaction.brokerPercentage || 0) || 0;
                  const brokerFee = brokerPct > 0 ? Math.round((base * brokerPct / 100) * 100) / 100 : 0;
                  return (
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div className="text-gray-600 dark:text-gray-300">Total a cobrar</div>
                      <div className="text-right font-semibold text-gray-900 dark:text-gray-100">${totalToCollect.toLocaleString('es-ES')}</div>
                      <div className="text-gray-600 dark:text-gray-300">Total a entregar</div>
                      <div className="text-right font-semibold text-gray-900 dark:text-gray-100">${totalToDeliver.toLocaleString('es-ES')}</div>
                      <div className="text-gray-600 dark:text-gray-300">Nuestra comisión (interna)</div>
                      <div className="text-right text-emerald-600 dark:text-emerald-300">${ourFee.toLocaleString('es-ES')}</div>
                      {externalCharges > 0 && (<>
                        <div className="text-gray-600 dark:text-gray-300">Cargos externos</div>
                        <div className="text-right text-amber-600 dark:text-amber-300">${externalCharges.toLocaleString('es-ES')}</div>
                      </>)}
                      {brokerFee > 0 && (<>
                        <div className="text-gray-600 dark:text-gray-300">Comisión broker (informativa)</div>
                        <div className="text-right text-blue-600 dark:text-blue-300">${brokerFee.toLocaleString('es-ES')}</div>
                      </>)}
                    </div>
                  );
                })() : (() => {
                  const breakdown = computeOrderChargeBreakdown(newTransaction);
                  const { base, external, internal, cashNeeded } = breakdown;
                  return (
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div className="text-gray-600 dark:text-gray-300">Monto base</div>
                      <div className="text-right text-gray-900 dark:text-gray-100">${base.toLocaleString('en-US')}</div>
                      <div className="text-gray-600 dark:text-gray-300">Cargos externos</div>
                      <div className="text-right text-amber-600 dark:text-amber-300">${external.toLocaleString('en-US')}</div>
                      <div className="text-gray-600 dark:text-gray-300">Comisión interna</div>
                      <div className="text-right text-red-600 dark:text-red-300">-${internal.toLocaleString('en-US')}</div>
                      <div className="text-gray-700 dark:text-gray-200 font-medium">Impacto en caja</div>
                      <div className="text-right font-semibold text-gray-900 dark:text-gray-100">${cashNeeded.toLocaleString('en-US')}</div>
                    </div>
                  );
                })()}
              </div>
            </div>
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3 xl:flex-1 xl:justify-end">
                {/* Date range filter */}
                <label className="flex w-full items-center gap-2 text-sm text-gray-600 dark:text-gray-300 sm:w-auto">
                  <Calendar className="h-4 w-4 text-gray-400" />
                  <select
                    value={dateRangeType}
                    onChange={(e) => {
                      const v = e.target.value;
                      setDateRangeType(v);
                      // Reset pagination when filters change
                      setPage(1);
                    }}
                    className="w-full rounded-full border border-gray-200 bg-white px-2.5 py-1.5 text-xs text-gray-700 transition focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-200 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100 dark:focus:border-blue-500 dark:focus:ring-blue-400/40 sm:w-44"
                  >
                    <option value="all">Todo el periodo</option>
                    <option value="today">Hoy</option>
                    <option value="7d">Últimos 7 días</option>
                    <option value="14d">Últimos 14 días</option>
                    <option value="30d">Últimos 30 días</option>
                    <option value="90d">Últimos 90 días</option>
                    <option value="custom">Personalizado…</option>
                  </select>
                </label>
                {dateRangeType === 'custom' && (
                  <div className="flex items-center gap-2">
                    <input
                      type="date"
                      value={customStart}
                      onChange={(e) => { setCustomStart(e.target.value); setPage(1); }}
                      className="rounded-full border border-gray-200 bg-white px-2.5 py-1.5 text-xs text-gray-700 transition focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-200 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100 dark:focus:border-blue-500 dark:focus:ring-blue-400/40"
                    />
                    <span className="text-gray-400">—</span>
                    <input
                      type="date"
                      value={customEnd}
                      onChange={(e) => { setCustomEnd(e.target.value); setPage(1); }}
                      className="rounded-full border border-gray-200 bg-white px-2.5 py-1.5 text-xs text-gray-700 transition focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-200 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100 dark:focus:border-blue-500 dark:focus:ring-blue-400/40"
                    />
                  </div>
                )}
              {/* Export */}
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setShowExportMenu((v)=>!v)}
                  className="inline-flex items-center gap-2 rounded-full border border-gray-300 bg-white px-2.5 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
                  aria-label="Exportar"
                >
                  <Download className="h-4 w-4" />
                  <span className="hidden md:inline">Exportar</span>
                </button>
                {showExportMenu && (
                  <div className="absolute right-0 z-20 mt-2 min-w-[160px] rounded-lg border border-gray-200 bg-white p-1 dark:border-gray-700 dark:bg-gray-800">
                    <button onClick={handleExportCSV} className="w-full text-left rounded-md px-3 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700">CSV</button>
                    <button onClick={handleExportXLSX} className="w-full text-left rounded-md px-3 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700">Excel (.xlsx)</button>
                  </div>
                )}
              </div>
              <label className="flex w-full items-center gap-2 text-sm text-gray-600 dark:text-gray-300 sm:w-auto">
                <Filter className="h-4 w-4 text-gray-400" />
                <select
                  value={statusFilter}
                  onChange={(event) => setStatusFilter(event.target.value)}
                  className="w-full rounded-full border border-gray-200 bg-white px-2.5 py-1.5 text-xs text-gray-700 transition focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-200 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100 dark:focus:border-blue-500 dark:focus:ring-blue-400/40 sm:w-44"
                >
                  {statusOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="flex w-full items-center gap-2 text-sm text-gray-600 dark:text-gray-300 sm:w-auto">
                <SlidersHorizontal className="h-4 w-4 text-gray-400" />
                <select
                  value={currentSortValue}
                  onChange={(event) => handleSortChange(event.target.value)}
                  className="w-full rounded-full border border-gray-200 bg-white px-2.5 py-1.5 text-xs text-gray-700 transition focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-200 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100 dark:focus:border-blue-500 dark:focus:ring-blue-400/40 sm:w-44"
                >
                  {sortOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          </div>
          {/* Chips row */}
          <div className="mt-3 flex flex-wrap items-center gap-1.5">
            {/* Active filter chips */}
            {typeFilter !== 'all' && (
              <button onClick={() => setTypeFilter('all')} className="inline-flex items-center gap-1.5 rounded-full bg-blue-50 px-2.5 py-1 text-[11px] font-medium text-blue-700 dark:bg-blue-900/30 dark:text-blue-200">
                Tipo: {typeFilter === 'sale' ? 'Venta' : typeFilter === 'order' ? 'Pedido' : 'Transferencia'} <X className="h-3.5 w-3.5" />
              </button>
            )}
            {statusFilter !== 'all' && (
              <button onClick={() => setStatusFilter('all')} className="inline-flex items-center gap-1.5 rounded-full bg-gray-100 px-2.5 py-1 text-[11px] font-medium text-gray-700 dark:bg-gray-800 dark:text-gray-200">
                Estado: {statusOptions.find(o=>o.value===statusFilter)?.label || statusFilter} <X className="h-3.5 w-3.5" />
              </button>
            )}
            {dateRangeType !== 'all' && (
              <button onClick={() => { setDateRangeType('all'); setCustomStart(''); setCustomEnd(''); }} className="inline-flex items-center gap-1.5 rounded-full bg-indigo-50 px-2.5 py-1 text-[11px] font-medium text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-200">
                Fecha: {dateRangeType === 'today' ? 'Hoy' : dateRangeType === 'custom' ? `${customStart || '—'} — ${customEnd || '—'}` : dateRangeType.toUpperCase()} <X className="h-3.5 w-3.5" />
              </button>
            )}
            {selectedBranchIds.map((id) => (
              <button key={`b-${id}`} onClick={() => setSelectedBranchIds(prev=>prev.filter(v=>v!==id))} className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 px-2.5 py-1 text-[11px] font-medium text-amber-700 dark:bg-amber-900/30 dark:text-amber-200">
                Sucursal: {branchMap[id] || id} <X className="h-3.5 w-3.5" />
              </button>
            ))}
            {selectedClientIds.map((id) => (
              <button key={`c-${id}`} onClick={() => setSelectedClientIds(prev=>prev.filter(v=>v!==id))} className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-1 text-[11px] font-medium text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-200">
                Cliente: {clientMap[id] || id} <X className="h-3.5 w-3.5" />
              </button>
            ))}
            {selectedSupplierIds.map((id) => (
              <button key={`s-${id}`} onClick={() => setSelectedSupplierIds(prev=>prev.filter(v=>v!==id))} className="inline-flex items-center gap-1.5 rounded-full bg-purple-50 px-2.5 py-1 text-[11px] font-medium text-purple-700 dark:bg-purple-900/30 dark:text-purple-200">
                Proveedor: {supplierMap[id] || id} <X className="h-3.5 w-3.5" />
              </button>
            ))}
            {selectedBrokerIds.map((id) => (
              <button key={`br-${id}`} onClick={() => setSelectedBrokerIds(prev=>prev.filter(v=>v!==id))} className="inline-flex items-center gap-1.5 rounded-full bg-sky-50 px-2.5 py-1 text-[11px] font-medium text-sky-700 dark:bg-sky-900/30 dark:text-sky-200">
                Broker: {brokerMap[id] || id} <X className="h-3.5 w-3.5" />
              </button>
            ))}
            {commissionPaidFilter !== 'all' && (
              <button onClick={() => setCommissionPaidFilter('all')} className="inline-flex items-center gap-1.5 rounded-full bg-rose-50 px-2.5 py-1 text-[11px] font-medium text-rose-700 dark:bg-rose-900/30 dark:text-rose-200">
                Comisión broker: {commissionPaidFilter === 'paid' ? 'Pagada' : 'Pendiente'} <X className="h-3.5 w-3.5" />
              </button>
            )}
            {amountMin && (
              <button onClick={() => setAmountMin('')} className="inline-flex items-center gap-1.5 rounded-full bg-gray-100 px-2.5 py-1 text-[11px] font-medium text-gray-700 dark:bg-gray-800 dark:text-gray-200">
                Monto ≥ ${Number(amountMin).toLocaleString('es-ES')} <X className="h-3.5 w-3.5" />
              </button>
            )}
            {amountMax && (
              <button onClick={() => setAmountMax('')} className="inline-flex items-center gap-1.5 rounded-full bg-gray-100 px-2.5 py-1 text-[11px] font-medium text-gray-700 dark:bg-gray-800 dark:text-gray-200">
                Monto ≤ ${Number(amountMax).toLocaleString('es-ES')} <X className="h-3.5 w-3.5" />
              </button>
            )}
            {depositAccountFilter && (
              <button onClick={() => setDepositAccountFilter('')} className="inline-flex items-center gap-1.5 rounded-full bg-fuchsia-50 px-2.5 py-1 text-[11px] font-medium text-fuchsia-700 dark:bg-fuchsia-900/30 dark:text-fuchsia-200">
                Depósito: {depositAccountFilter} <X className="h-3.5 w-3.5" />
              </button>
            )}
            {/* Toggle advanced filter panel */}
            <button onClick={() => setShowAdvancedFilters(v=>!v)} className="inline-flex items-center gap-1.5 rounded-full border border-gray-300 px-2.5 py-1 text-[11px] font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700">
              <SlidersHorizontal className="h-3.5 w-3.5"/>
              <span className="hidden sm:inline">Más filtros</span>
              {showAdvancedFilters ? <ChevronDown className="h-3.5 w-3.5"/> : <ChevronRight className="h-3.5 w-3.5"/>}
            </button>
            {(typeFilter !== 'all' || statusFilter !== 'all' || dateRangeType !== 'all' || selectedBranchIds.length || selectedClientIds.length || selectedSupplierIds.length || selectedBrokerIds.length || commissionPaidFilter !== 'all' || amountMin || amountMax || depositAccountFilter) && (
              <button
                onClick={() => {
                  setTypeFilter('all');
                  setStatusFilter('all');
                  setDateRangeType('all'); setCustomStart(''); setCustomEnd('');
                  setSelectedBranchIds([]); setSelectedClientIds([]); setSelectedSupplierIds([]); setSelectedBrokerIds([]);
                  setCommissionPaidFilter('all'); setAmountMin(''); setAmountMax(''); setDepositAccountFilter(''); setPage(1);
                }}
                className="inline-flex items-center gap-1.5 rounded-full border border-red-300 px-2.5 py-1 text-[11px] font-medium text-red-600 hover:bg-red-50 dark:border-red-600/60 dark:text-red-300 dark:hover:bg-red-900/20"
              >
                Limpiar todo
              </button>
            )}
            {/* Type filter chips placed below filters/exports, right-aligned */}
            <div className="flex items-center gap-1.5 ml-auto">
              {['all', 'sale', 'order', 'transfer'].map((type) => (
                <button
                  key={type}
                  onClick={() => setTypeFilter(type)}
                  className={cn(
                    'rounded-full px-2.5 py-1 text-xs font-medium transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2',
                    typeFilter === type
                      ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-200'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700'
                  )}
                >
                  {type === 'all' ? 'Todas' : type === 'sale' ? 'Ventas' : type === 'order' ? 'Pedidos' : 'Transferencias'}
                </button>
              ))}
            </div>
          </div>
          {showAdvancedFilters && (
            <div className="mt-3 grid gap-3 md:grid-cols-2 lg:grid-cols-3">
              <label className="text-sm text-gray-600 dark:text-gray-300">
                Sucursales
                <div className="mt-1 flex flex-wrap gap-2">
                  {(branches || []).map((b) => {
                    const id = String(b.id);
                    const active = selectedBranchIds.includes(id);
                    return (
                      <button
                        key={id}
                        onClick={() => setSelectedBranchIds((prev)=> active ? prev.filter(v=>v!==id) : [...prev, id])}
                        className={cn('rounded-full px-3 py-1 text-xs', active ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200' : 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-200')}
                      >
                        {b.name || id}
                      </button>
                    );
                  })}
                </div>
              </label>
              <label className="text-sm text-gray-600 dark:text-gray-300">
                Clientes
                <div className="mt-1 flex flex-wrap gap-2 max-h-40 overflow-y-auto pr-1">
                  {clients.map((c) => {
                    const id = String(c.id);
                    const active = selectedClientIds.includes(id);
                    return (
                      <button key={id} onClick={() => setSelectedClientIds((prev)=> active ? prev.filter(v=>v!==id) : [...prev, id])} className={cn('rounded-full px-3 py-1 text-xs', active ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200' : 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-200')}>{c.name || c.company_name || id}</button>
                    );
                  })}
                </div>
              </label>
              <label className="text-sm text-gray-600 dark:text-gray-300">
                Proveedores
                <div className="mt-1 flex flex-wrap gap-2 max-h-40 overflow-y-auto pr-1">
                  {suppliers.map((s) => {
                    const id = String(s.id);
                    const active = selectedSupplierIds.includes(id);
                    return (
                      <button key={id} onClick={() => setSelectedSupplierIds((prev)=> active ? prev.filter(v=>v!==id) : [...prev, id])} className={cn('rounded-full px-3 py-1 text-xs', active ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-200' : 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-200')}>{s.name || id}</button>
                    );
                  })}
                </div>
              </label>
              <label className="text-sm text-gray-600 dark:text-gray-300">
                Brokers
                <div className="mt-1 flex flex-wrap gap-2 max-h-40 overflow-y-auto pr-1">
                  {brokers.map((b) => {
                    const id = String(b.id);
                    const active = selectedBrokerIds.includes(id);
                    return (
                      <button key={id} onClick={() => setSelectedBrokerIds((prev)=> active ? prev.filter(v=>v!==id) : [...prev, id])} className={cn('rounded-full px-3 py-1 text-xs', active ? 'bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-200' : 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-200')}>{b.name || id}</button>
                    );
                  })}
                </div>
              </label>
              <label className="text-sm text-gray-600 dark:text-gray-300">
                Comisión broker
                <select value={commissionPaidFilter} onChange={(e)=>setCommissionPaidFilter(e.target.value)} className="mt-1 w-full rounded-full border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700  transition focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-200 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100 dark:focus:border-blue-500 dark:focus:ring-blue-400/40">
                  <option value="all">Todas</option>
                  <option value="paid">Pagadas</option>
                  <option value="pending">Pendientes</option>
                </select>
              </label>
              <label className="text-sm text-gray-600 dark:text-gray-300">
                Rango de monto
                <div className="mt-1 flex items-center gap-2">
                  <input type="number" placeholder="Mín" value={amountMin} onChange={(e)=>setAmountMin(e.target.value)} className="w-full rounded-full border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-200 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100" />
                  <span className="text-gray-400">—</span>
                  <input type="number" placeholder="Máx" value={amountMax} onChange={(e)=>setAmountMax(e.target.value)} className="w-full rounded-full border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-200 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100" />
                </div>
              </label>
              <label className="text-sm text-gray-600 dark:text-gray-300">
                Destino de depósito
                <input type="text" placeholder="Nombre del destino" value={depositAccountFilter} onChange={(e)=>setDepositAccountFilter(e.target.value)} className="mt-1 w-full rounded-full border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-200 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100" />
              </label>
              <div className="md:col-span-2 flex gap-2 justify-end">
                <button type="button" onClick={() => { setShowAdvancedFilters(false); setPage(1); }} className="rounded-full border border-blue-300 px-3 py-1 text-xs font-medium text-blue-700 hover:bg-blue-50 dark:border-blue-500/60 dark:text-blue-200 dark:hover:bg-blue-900/20">Aplicar</button>
                <button type="button" onClick={() => setShowAdvancedFilters(false)} className="rounded-full border border-gray-300 px-3 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700">Cerrar</button>
              </div>
            </div>
          )}
        </div>

        {selectedCount > 0 && (
          <StateMessage
            intent="info"
            title={`${selectedCount} operación${selectedCount > 1 ? 'es' : ''} seleccionada${selectedCount > 1 ? 's' : ''}`}
            description="Aplica acciones masivas para agilizar tu flujo de trabajo."
            actions={[
              <button
                key="bulk-delete"
                onClick={handleBulkDelete}
                className="inline-flex items-center gap-2 rounded-full border border-red-400 bg-red-50 px-3 py-1 text-xs font-semibold text-red-600 transition hover:bg-red-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-400 dark:border-red-500/60 dark:bg-red-500/10 dark:text-red-200"
              >
                Eliminar seleccionadas
              </button>,
              eligibleCommissionCount > 0 && (
                <button
                  key="bulk-paid"
                  onClick={() => handleBulkCommissionToggle(true)}
                  className="inline-flex items-center gap-2 rounded-full border border-emerald-400 bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-600 transition hover:bg-emerald-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-emerald-400 dark:border-emerald-500/60 dark:bg-emerald-500/10 dark:text-emerald-200"
                >
                  Marcar comisiones pagadas
                </button>
              ),
              eligibleCommissionCount > 0 && (
                <button
                  key="bulk-unpaid"
                  onClick={() => handleBulkCommissionToggle(false)}
                  className="inline-flex items-center gap-2 rounded-full border border-amber-400 bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-600 transition hover:bg-amber-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-amber-400 dark:border-amber-500/60 dark:bg-amber-500/10 dark:text-amber-200"
                >
                  Marcar comisiones pendientes
                </button>
              ),
            ].filter(Boolean)}
          />
        )}

        <div className="rounded-2xl border border-gray-200 bg-white  dark:border-gray-800 dark:bg-gray-900 neon-border">
          {isLoading ? (
            <div className="space-y-3 p-6">
              {Array.from({ length: 6 }).map((_, index) => (
                <Skeleton key={index} className="h-12 w-full" />
              ))}
            </div>
          ) : filteredTransactions.length === 0 ? (
            <div className="p-12">
              <EmptyState
                title="No hay operaciones disponibles"
                description={emptyDescription}
                action={
                  <button
                    onClick={handleStartCreate}
                    className="inline-flex items-center gap-2 rounded-full bg-blue-600 px-4 py-2 text-sm font-semibold text-white  transition hover:bg-blue-700"
                  >
                    <Plus className="h-4 w-4" />
                    Crear operación
                  </button>
                }
              />
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-800">
                  <thead className="bg-gray-50/70 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 dark:bg-gray-800/60 dark:text-gray-300">
                    <tr>
                      <th className="w-12 px-4 py-3">
                        <input
                          type="checkbox"
                          checked={allPageSelected}
                          onChange={toggleSelectAll}
                          aria-label="Seleccionar página"
                          className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-800"
                        />
                      </th>
                      <th className="px-4 py-3">Fecha</th>
                      <th className="px-4 py-3">Tipo</th>
                      <th className="px-4 py-3">Participantes</th>
                      <th className="px-4 py-3">Montos</th>
                      <th className="px-4 py-3">Depósitos</th>
                      <th className="px-4 py-3">Estado</th>
                      <th className="px-4 py-3 text-right">Acciones</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 text-sm text-gray-700 dark:divide-gray-800 dark:text-gray-100">
                    {pageRows.map((tx) => {
                      const isSelected = selectedSet.has(String(tx.id));
                      const typeValue = (tx.type || tx.transaction_type || '').toString();
                      const clientName = clientMap[String(tx.clientId ?? tx.client_id)] || '—';
                      const supplierName = supplierMap[String(tx.supplierId ?? tx.supplier_id)] || '—';
                      const brokerName = brokerMap[String(tx.brokerId ?? tx.broker_id)] || null;
                      const depositsSum = Array.isArray(tx.deposits)
                        ? tx.deposits.reduce((sum, deposit) => sum + (Number(deposit.amount || 0) || 0), 0)
                        : Number(tx.depositAmount || 0);
                      const branchName = branchMap[String(tx.branchId ?? tx.branch_id)] || '—';
                      const sourceBranchName = branchMap[String(tx.sourceBranchId ?? tx.source_branch_id ?? tx.branchId ?? tx.branch_id)] || branchName;
                      const targetBranchName = branchMap[String(tx.targetBranchId ?? tx.target_branch_id ?? tx.metadata?.transfer?.targetBranchId ?? tx.metadata?.targetBranchId ?? '')] || null;
                      const typeLabel = typeValue === 'sale' ? 'Venta' : typeValue === 'transfer' ? 'Transferencia' : 'Pedido';
                      const typeBadgeClass = typeValue === 'sale'
                        ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300'
                        : typeValue === 'transfer'
                          ? 'bg-purple-100 text-purple-700 dark:bg-purple-500/10 dark:text-purple-300'
                          : 'bg-blue-100 text-blue-700 dark:bg-blue-500/10 dark:text-blue-300';
                      const isExpanded = expandedRows.has(tx.id);
                      const charges = getOrderChargesArray(tx);
                      const hasCharges = charges.length > 0;
                      return (
                        <React.Fragment key={tx.id}>
                        <tr
                          className={cn(
                            'group transition',
                            isSelected
                              ? 'bg-blue-50/70 dark:bg-blue-900/30'
                              : 'hover:bg-gray-50/80 dark:hover:bg-gray-800/70'
                          )}
                        >
                          <td className="px-4 py-3 align-middle">
                            <div className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => toggleSelectTransaction(tx.id)}
                              aria-label={`Seleccionar operación ${tx.id}`}
                              className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-800"
                            />
                              {hasCharges && (
                                <button
                                  onClick={() => toggleRowExpansion(tx.id)}
                                  className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                                  aria-label={isExpanded ? 'Colapsar' : 'Expandir'}
                                >
                                  {isExpanded ? (
                                    <ChevronDown className="h-4 w-4 text-gray-500" />
                                  ) : (
                                    <ChevronRight className="h-4 w-4 text-gray-500" />
                                  )}
                                </button>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-3 align-middle">
                            {(() => {
                              const raw = tx.date || tx.created_at;
                              if (!raw) return (<><div className="font-medium text-gray-900 dark:text-gray-50">—</div><div className="text-xs text-gray-500 dark:text-gray-400">{tx.createdByEmail || tx.id}</div></>);
                              const d = new Date(raw);
                              const display = Number.isNaN(d.getTime()) ? String(raw) : d.toISOString().split('T')[0];
                              return (
                                <>
                                  <div className="font-medium text-gray-900 dark:text-gray-50">{display}</div>
                                  <div className="text-xs text-gray-500 dark:text-gray-400">{tx.createdByEmail || tx.id}</div>
                                </>
                              );
                            })()}
                          </td>
                          <td className="px-4 py-3 align-middle">
                            <span
                              className={cn(
                                'inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold uppercase tracking-wide',
                                typeBadgeClass
                              )}
                            >
                              {typeLabel}
                            </span>
                            {brokerName && (
                              <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">Broker: {brokerName}</div>
                            )}
                          </td>
                          <td className="px-4 py-3 align-middle">
                            {typeValue === 'sale' && (
                              <>
                                <div className="font-medium">{clientName}</div>
                                <div className="text-xs text-gray-500 dark:text-gray-400">Sucursal: {branchName}</div>
                                {tx.depositAccount && (
                                  <div className="text-xs text-gray-500 dark:text-gray-400">Depósito {tx.depositAccount}</div>
                                )}
                              </>
                            )}
                            {typeValue === 'order' && (
                              <>
                                <div className="font-medium">Proveedor: {supplierName}</div>
                                <div className="text-xs text-gray-500 dark:text-gray-400">Sucursal: {branchName}</div>
                                {tx.description && (
                                  <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">{tx.description}</div>
                                )}
                              </>
                            )}
                            {typeValue === 'transfer' && (
                              <>
                                <div className="font-medium text-gray-900 dark:text-gray-100">Transferencia interna</div>
                                <div className="text-xs text-gray-500 dark:text-gray-400">Origen: {sourceBranchName || '—'}</div>
                                <div className="text-xs text-gray-500 dark:text-gray-400">Destino: {targetBranchName || '—'}</div>
                                {tx.description && (
                                  <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">{tx.description}</div>
                                )}
                              </>
                            )}
                          </td>
                          <td className="px-4 py-3 align-middle">
                            {(() => {
                              const base = tx.amount != null ? Number(tx.amount) : null;
                              const isOrderLike = typeValue === 'order' || typeValue === 'transfer';
                              const isSale = typeValue === 'sale';
                              const breakdown = isOrderLike ? computeOrderChargeBreakdown(tx) : null;
                              const brokerCommission = Number(tx.brokerCommission || 0) || 0;
                              const profitRow = Array.isArray(transactionProfits)
                                ? transactionProfits.find((p) => String(p.transaction_id) === String(tx.id))
                                : null;
                              const netProfit = profitRow ? Number(profitRow.net_profit || 0) : 0;
                              const profitClass = netProfit > 0
                                ? 'text-emerald-600 dark:text-emerald-300'
                                : netProfit < 0
                                  ? 'text-red-600 dark:text-red-300'
                                  : 'text-gray-600 dark:text-gray-300';
                              return (
                                <>
                                  <div className="font-semibold text-gray-900 dark:text-gray-50">
                                    {base != null ? `$${Number(base).toLocaleString('es-ES')}` : '—'}
                                  </div>
                                  {isSale && (() => {
                                    const sale = computeSaleChargeBreakdown(tx, { baseAmount: Number(base || 0) });
                                    const totalToCollect = Number(sale.totalToCollect || 0);
                                    const totalToDeliver = Number(sale.totalToDeliver || 0);
                                    return (
                                      <div className="mt-1 space-y-0.5">
                                        <div className="text-xs text-gray-600 dark:text-gray-300">Total a cobrar: <span className="font-medium text-gray-900 dark:text-gray-100">${totalToCollect.toLocaleString('es-ES')}</span></div>
                                        <div className="text-xs text-gray-600 dark:text-gray-300">Total a entregar: <span className="font-medium text-gray-900 dark:text-gray-100">${totalToDeliver.toLocaleString('es-ES')}</span></div>
                                      </div>
                                    );
                                  })()}
                                  {isSale && brokerCommission > 0 && (
                                    <div className="text-xs text-amber-600 dark:text-amber-300">
                                      Comisión broker: ${brokerCommission.toLocaleString('es-ES')}
                                    </div>
                                  )}
                                  {isOrderLike && breakdown && breakdown.external > 0 && (
                                    <div className="text-xs text-red-600 dark:text-red-300">
                                      Cargos ext.: -${breakdown.external.toLocaleString('es-ES')}
                                    </div>
                                  )}
                                  {isOrderLike && breakdown && breakdown.internal > 0 && (
                                    <div className={`text-xs ${typeValue === 'transfer' ? 'text-emerald-600 dark:text-emerald-300' : 'text-red-600 dark:text-red-300'}`}>
                                      {typeValue === 'transfer'
                                        ? `Comisión int.: +$${breakdown.internal.toLocaleString('es-ES')}`
                                        : `Comisión int.: -$${breakdown.internal.toLocaleString('es-ES')}`}
                                    </div>
                                  )}
                                  {isOrderLike && breakdown && (
                                    <div className="text-xs text-gray-600 dark:text-gray-300">
                                      Impacto neto: ${breakdown.cashNeeded.toLocaleString('es-ES')}
                                    </div>
                                  )}
                                  {isSale && (
                                    <div className={`text-xs ${profitClass}`}>
                                      Ganancias: ${netProfit.toLocaleString('es-ES')}
                                    </div>
                                  )}
                                </>
                              );
                            })()}
                          </td>
                          <td className="px-4 py-3 align-middle">
                            <div className="font-medium">{depositsSum ? `$${Number(depositsSum).toLocaleString('en-US')}` : '—'}</div>
                            {Array.isArray(tx.deposits) && tx.deposits.length > 0 && (
                              <div className="text-xs text-gray-500 dark:text-gray-400">{tx.deposits.length} registros</div>
                            )}
                          </td>
                          <td className="px-4 py-3 align-middle">
                            <span
                              className={cn(
                                'inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold uppercase tracking-wide',
                                getStatusClass(tx.status)
                              )}
                            >
                              {getStatusLabel(tx.status, tx.type)}
                            </span>
                            {tx.type === 'sale' && Number(tx.brokerCommission ?? tx.broker_commission ?? 0) > 0 && (
                              <button
                                onClick={() => handleToggleCommissionStatus(tx)}
                                className="mt-2 inline-flex rounded-full border border-dashed px-2 py-0.5 text-[11px] font-medium text-blue-600 transition hover:border-blue-500 hover:text-blue-700 dark:border-blue-300/40 dark:text-blue-200 dark:hover:border-blue-300 dark:hover:text-blue-100"
                              >
                                {Boolean(tx.brokerCommissionPaid ?? tx.broker_commission_paid)
                                  ? 'Comisión pagada'
                                  : 'Comisión pendiente'}
                              </button>
                            )}
                          </td>
                          <td className="px-4 py-3 align-middle text-right">
                            <div className="flex flex-wrap items-center justify-end gap-2">
                              <button
                                onClick={() => openView(tx)}
                                className="rounded-full border border-gray-300 px-3 py-1 text-xs font-medium text-gray-600 transition hover:bg-gray-100 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
                              >
                                Ver
                              </button>
                              <button
                                onClick={() => openEdit(tx)}
                                className="rounded-full border border-gray-300 px-3 py-1 text-xs font-medium text-gray-600 transition hover:bg-gray-100 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
                              >
                                Editar
                              </button>
                              <button
                                onClick={() => openAddDeposit(tx.id)}
                                className="rounded-full border border-gray-300 px-3 py-1 text-xs font-medium text-gray-600 transition hover:bg-gray-100 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
                              >
                                Agregar depósito
                              </button>
                              <button
                                onClick={() => openDepositHistory(tx)}
                                className="rounded-full border border-gray-300 px-3 py-1 text-xs font-medium text-gray-600 transition hover:bg-gray-100 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
                              >
                                Ver depósitos
                              </button>
                              {tx.status !== 'cancelled' && (
                                <button
                                  onClick={() => prepareDelivery(tx)}
                                  className="rounded-full bg-emerald-600 px-3 py-1 text-xs font-semibold text-white  transition hover:bg-emerald-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-emerald-500"
                                >
                                  {typeValue === 'order' ? 'Registrar recibido' : typeValue === 'transfer' ? 'Registrar recepción' : 'Registrar entrega'}
                                </button>
                              )}
                              {typeValue !== 'sale' && (tx.status === 'pending' || tx.status === 'pending_deposit') && (
                                <button
                                  onClick={() => handleCancelOrderLocal(tx.id)}
                                  className="rounded-full bg-amber-500 px-3 py-1 text-xs font-semibold text-white  transition hover:bg-amber-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-amber-500"
                                >
                                  Cancelar
                                </button>
                              )}
                              <button
                                onClick={() => handleDeleteTransactionClick(tx)}
                                className="rounded-full border border-red-500 px-3 py-1 text-xs font-semibold text-red-600 transition hover:bg-red-50 dark:border-red-400/70 dark:text-red-300 dark:hover:bg-red-500/10"
                              >
                                Eliminar
                              </button>
                            </div>
                          </td>
                        </tr>
                        {isExpanded && hasCharges && (
                          <tr key={`${tx.id}-charges`} className="bg-gray-50/50 dark:bg-gray-800/30">
                            <td colSpan="8" className="px-4 py-3">
                              <div className="pl-12">
                                <div className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-2">
                                  {typeValue === 'sale' ? 'Comisiones de venta' : typeValue === 'order' ? 'Cargos del pedido' : 'Cargos de transferencia'}
                                </div>
                                <div className="space-y-2">
                                  {charges.map((charge, idx) => {
                                    const isPercent = (charge.type || 'flat') === 'percent';
                                    const val = Number(charge.value || 0) || 0;
                                    const kind = resolveChargeKind(charge);
                                    const isInternal = kind === 'internal';
                                    const baseAmount = Number(tx.amount || 0);
                                    const computed = isPercent ? Math.round((baseAmount * val) / 100 * 100) / 100 : val;
                                    
                                    return (
                                      <div key={idx} className="flex items-center justify-between py-1.5 px-3 rounded-lg bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700">
                                        <div className="flex items-center gap-3">
                                          <div className="w-2 h-2 rounded-full" style={{ backgroundColor: isInternal ? '#3b82f6' : '#f59e0b' }} />
                                          <div>
                                            <div className="text-sm font-medium text-gray-700 dark:text-gray-200">
                                              {isInternal ? 'Comisión interna' : 'Cargo externo'}
                                            </div>
                                            <div className="text-xs text-gray-500 dark:text-gray-400">
                                              {isPercent ? `${val}% del monto base` : 'Monto fijo'}
                                            </div>
                                          </div>
                                        </div>
                                        <div className="text-right">
                                          <div className={`text-sm font-semibold ${isInternal && typeValue === 'order' ? 'text-green-600 dark:text-green-400' : 'text-gray-900 dark:text-gray-100'}`}>
                                            {isInternal && typeValue === 'order' ? '+' : ''}${computed.toLocaleString('es-ES')}
                                          </div>
                                          <div className="text-xs text-gray-500 dark:text-gray-400">
                                            {typeValue === 'order' ? (isInternal ? 'Incrementa caja' : 'Cliente paga') : (isInternal ? 'Ganancia' : 'Cargo adicional')}
                                          </div>
                                        </div>
                                      </div>
                                    );
                                  })}
                                  {typeValue === 'sale' && tx.brokerCommission > 0 && (
                                    <div className="flex items-center justify-between py-1.5 px-3 rounded-lg bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700">
                                      <div className="flex items-center gap-3">
                                        <div className="w-2 h-2 rounded-full bg-purple-500" />
                                        <div>
                                          <div className="text-sm font-medium text-gray-700 dark:text-gray-200">
                                            Comisión broker
                                          </div>
                                          <div className="text-xs text-gray-500 dark:text-gray-400">
                                            {brokerName} - {tx.brokerPercentage || 0}%
                                          </div>
                                        </div>
                                      </div>
                                      <div className="text-right">
                                        <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                                          -${Number(tx.brokerCommission).toLocaleString('es-ES')}
                                        </div>
                                        <div className="text-xs text-gray-500 dark:text-gray-400">
                                          A pagar al broker
                                        </div>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="border-t border-gray-100 dark:border-gray-800 px-4 py-3">
                <Pagination page={page} pageSize={pageSize} total={filteredTransactions.length} onPageChange={setPage} />
              </div>
            </>
          )}
        </div>
      </div>

      <ConfirmDialog
        open={confirmDialog.open}
        title={confirmDialog.title}
        description={confirmDialog.description}
        confirmLabel={confirmDialog.confirmLabel}
        intent={confirmDialog.intent}
        destructive={confirmDialog.destructive}
        onConfirm={() => confirmDialog.onConfirm?.()}
        onCancel={closeConfirmDialog}
      />

      {/* Modals (crear/editar, depósitos, historial, entrega, confirm) */}
      {showModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50">
          <div className="mx-auto mt-16 w-full max-w-lg rounded-xl border border-gray-200 bg-white p-5 dark:border-gray-700 dark:bg-gray-800">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                {viewOnlyMode ? 'Ver operación' : (editingTransactionId ? 'Editar operación' : 'Nueva operación')}
              </h3>
              <button type="button" onClick={closePrimaryModal} className="text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200">
                ✕
              </button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-3">
              <div className="space-y-2">
                <div>
                  <label className="block text-sm text-gray-700 dark:text-gray-200">Tipo de operación</label>
                  <select name="type" value={newTransaction.type} onChange={handleInputChange} disabled={Boolean(isEditing) || viewOnlyMode} className="w-full border p-2 rounded border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white disabled:opacity-70 disabled:cursor-not-allowed">
                    <option value="sale">Venta</option>
                    <option value="order">Pedido</option>
                    <option value="transfer">Transferencia interna</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm text-gray-700 dark:text-gray-200">{newTransaction.type === 'transfer' ? 'Sucursal origen' : 'Sucursal'}</label>
                  <select name="branchId" value={newTransaction.branchId} onChange={handleInputChange} disabled={viewOnlyMode} className="w-full border p-2 rounded border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white disabled:opacity-70 disabled:cursor-not-allowed">
                    <option value="">Selecciona…</option>
                    {branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                  </select>
                </div>
                {newTransaction.type === 'transfer' && (
                  <div>
                    <label className="block text-sm text-gray-700 dark:text-gray-200">Sucursal destino</label>
                    <select name="targetBranchId" value={newTransaction.targetBranchId ?? ''} onChange={handleInputChange} disabled={viewOnlyMode} className="w-full border p-2 rounded border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white disabled:opacity-70 disabled:cursor-not-allowed">
                      <option value="">Selecciona…</option>
                      {branches.map(b => (
                        <option key={b.id} value={b.id} disabled={String(b.id) === String(newTransaction.branchId || newTransaction.sourceBranchId)}>
                          {b.name}
                        </option>
                      ))}
                    </select>
                    <p className="mt-1 text-xs text-amber-600 dark:text-amber-300">Recuerda elegir una sucursal distinta a la de origen.</p>
                  </div>
                )}
                {newTransaction.type === 'sale' && (
                  <div className="relative client-search-container">
                    <label className="block text-sm text-gray-700 dark:text-gray-200">Cliente</label>
                    {viewOnlyMode ? (
                      <input
                        type="text"
                        value={clients.find(c => c.id === newTransaction.clientId)?.name || ''}
                        disabled
                        className="w-full border p-2 rounded border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white disabled:opacity-70 disabled:cursor-not-allowed"
                      />
                    ) : (
                      <>
                        <div className="relative">
                          <input
                            type="text"
                            value={clientSearchTerm || (newTransaction.clientId ? clients.find(c => c.id === newTransaction.clientId)?.name || '' : '')}
                            onChange={(e) => {
                              setClientSearchTerm(e.target.value);
                              setShowClientDropdown(true);
                              if (!e.target.value) {
                                setNewTransaction(prev => ({ ...prev, clientId: '' }));
                              }
                            }}
                            onFocus={() => setShowClientDropdown(true)}
                            placeholder="Buscar cliente..."
                            className="w-full border p-2 pr-8 rounded border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                          />
                          <Search className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        </div>
                        {showClientDropdown && (() => {
                          const searchLower = clientSearchTerm.toLowerCase();
                          const filteredClients = clients.filter(c =>
                            c.name.toLowerCase().includes(searchLower)
                          );
                          return filteredClients.length > 0 ? (
                            <div className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded shadow-lg max-h-60 overflow-y-auto">
                              {filteredClients.map(c => (
                                <div
                                  key={c.id}
                                  onClick={() => {
                                    // Call handleInputChange to trigger default charge application
                                    handleInputChange({ target: { name: 'clientId', value: c.id } });
                                    setClientSearchTerm('');
                                    setShowClientDropdown(false);
                                  }}
                                  className={cn(
                                    "p-2 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600",
                                    newTransaction.clientId === c.id && "bg-blue-50 dark:bg-blue-900/30"
                                  )}
                                >
                                  {c.name}
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded shadow-lg p-2 text-sm text-gray-500 dark:text-gray-400">
                              No se encontraron clientes
                            </div>
                          );
                        })()}
                      </>
                    )}
                  </div>
                )}
                {newTransaction.type === 'order' && (
                  <div>
                    <label className="block text-sm text-gray-700 dark:text-gray-200">Proveedor</label>
                    <select name="supplierId" value={newTransaction.supplierId} onChange={handleInputChange} disabled={viewOnlyMode} className="w-full border p-2 rounded border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white disabled:opacity-70 disabled:cursor-not-allowed">
                      <option value="">Selecciona…</option>
                      {suppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                    </select>
                  </div>
                )}
                {renderChargesEditor()}
                {newTransaction.type === 'sale' && (
                  <>
                    <div className="flex items-center gap-2">
                      <input
                        id="use-broker"
                        type="checkbox"
                        checked={useBroker}
                        disabled={viewOnlyMode}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          setUseBroker(checked);
                          if (!checked) {
                            setNewTransaction(prev => ({ ...prev, brokerId: '', brokerPercentage: '', brokerCommission: undefined }));
                          }
                        }}
                      />
                      <label htmlFor="use-broker" className="text-sm text-gray-700 dark:text-gray-200">Usar broker</label>
                    </div>
                    {useBroker && (
                      <>
                        <div>
                          <label className="block text-sm text-gray-700 dark:text-gray-200">Broker</label>
                          <select name="brokerId" value={newTransaction.brokerId} onChange={handleInputChange} disabled={viewOnlyMode} className="w-full border p-2 rounded border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white disabled:opacity-70 disabled:cursor-not-allowed">
                            <option value="">Selecciona…</option>
                            {brokers.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm text-gray-700 dark:text-gray-200">% Broker</label>
                          <input type="number" step="0.01" name="brokerPercentage" value={newTransaction.brokerPercentage} onChange={handleInputChange} disabled={viewOnlyMode} className="w-full border p-2 rounded border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white disabled:opacity-70 disabled:cursor-not-allowed" />
                        </div>
                      </>
                    )}
                  </>
                )}
                <div>
                  <label className="block text-sm text-gray-700 dark:text-gray-200">Monto</label>
                  <input type="number" name="amount" value={newTransaction.amount} onChange={handleInputChange} disabled={viewOnlyMode} className="w-full border p-2 rounded border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white disabled:opacity-70 disabled:cursor-not-allowed" />
                </div>
                {newTransaction.type === 'sale' && (
                  <div>
                    <label className="block text-sm text-gray-700 dark:text-gray-200">Destino de depósito</label>
                    <select name="depositAccount" value={newTransaction.depositAccount} onChange={handleInputChange} disabled={viewOnlyMode} className="w-full border p-2 rounded border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white disabled:opacity-70 disabled:cursor-not-allowed">
                      <option value="">Selecciona…</option>
                      {accountOptions.map((name) => (
                        <option key={name} value={name}>{name}</option>
                      ))}
                    </select>
                  </div>
                )}
                {/* Campo de monto de depósito eliminado por solicitud */}
                <div>
                  <label className="block text-sm text-gray-700 dark:text-gray-200">Descripción</label>
                  <input type="text" name="description" value={newTransaction.description || ''} onChange={handleInputChange} className="w-full border p-2 rounded border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white" />
                </div>
              </div>
              {/* Resumen de comisiones en modal (ventas) */}
              {newTransaction.type === 'sale' && (
                <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3 bg-gray-50 dark:bg-gray-800/50">
                  {(() => {
                    const base = Number(newTransaction.amount || 0) || 0;
                    const breakdown = computeSaleChargeBreakdown(newTransaction, { baseAmount: base });
                    const internalCharges = Number(breakdown.internal || 0) || 0;
                    const externalCharges = Number(breakdown.external || 0) || 0;
                    const totalToCollect = Number(breakdown.totalToCollect || (base + externalCharges));
                    const totalToDeliver = Number(breakdown.totalToDeliver || Math.max(0, base - internalCharges));
                    const brokerPct = Number(newTransaction.brokerPercentage || 0) || 0;
                    const brokerFee = brokerPct > 0 && base > 0 ? Math.round((base * brokerPct / 100) * 100) / 100 : 0;
                    return (
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div className="text-gray-600 dark:text-gray-300">Monto base</div>
                        <div className="text-right text-gray-900 dark:text-gray-100">${base.toLocaleString('es-ES')}</div>
                        <div className="text-gray-600 dark:text-gray-300">Comisión interna</div>
                        <div className="text-right text-emerald-600 dark:text-emerald-300">${internalCharges.toLocaleString('es-ES')}</div>
                        {externalCharges > 0 && (<>
                          <div className="text-gray-600 dark:text-gray-300">Cargos externos</div>
                          <div className="text-right text-amber-600 dark:text-amber-300">${externalCharges.toLocaleString('es-ES')}</div>
                        </>)}
                        {brokerFee > 0 && (<>
                          <div className="text-gray-600 dark:text-gray-300">Comisión broker</div>
                          <div className="text-right text-blue-600 dark:text-blue-300">${brokerFee.toLocaleString('es-ES')}</div>
                        </>)}
                        <div className="text-gray-700 dark:text-gray-200 font-medium">Total a cobrar</div>
                        <div className="text-right font-semibold text-gray-900 dark:text-gray-100">${totalToCollect.toLocaleString('es-ES')}</div>
                        <div className="text-gray-700 dark:text-gray-200 font-medium">Total a entregar</div>
                        <div className="text-right font-semibold text-gray-900 dark:text-gray-100">${totalToDeliver.toLocaleString('es-ES')}</div>
                      </div>
                    );
                  })()}
                </div>
              )}
              {/* Resumen de costos para pedidos/transferencias (al fondo) */}
              {['order', 'transfer'].includes(newTransaction.type) && (
                <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3 bg-gray-50 dark:bg-gray-800/50">
                  {(() => {
                    const breakdown = computeOrderChargeBreakdown(newTransaction);
                    const { base, external, internal, totalCost, cashNeeded } = breakdown;
                    const isTransfer = newTransaction.type === 'transfer';
                    const transferTotal = isTransfer ? (Number(base || 0) + Number(external || 0) + Number(internal || 0)) : null;
                    return (
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div className="text-gray-600 dark:text-gray-300">Monto base</div>
                        <div className="text-right text-gray-900 dark:text-gray-100">${base.toLocaleString('en-US')}</div>
                        <div className="text-gray-600 dark:text-gray-300">Cargos externos</div>
                        <div className="text-right text-amber-600 dark:text-amber-300">${external.toLocaleString('en-US')}</div>
                        <div className="text-gray-600 dark:text-gray-300">Comisión interna</div>
                        {isTransfer ? (
                          <div className="text-right text-emerald-600 dark:text-emerald-300">+${internal.toLocaleString('en-US')}</div>
                        ) : (
                          <div className="text-right text-red-600 dark:text-red-300">-${internal.toLocaleString('en-US')}</div>
                        )}
                        <div className="text-gray-700 dark:text-gray-200 font-medium">Costo total</div>
                        <div className="text-right font-semibold text-gray-900 dark:text-gray-100">${totalCost.toLocaleString('en-US')}</div>
                        {isTransfer && (
                          <>
                            <div className="text-gray-700 dark:text-gray-200 font-medium">Total transferencia</div>
                            <div className="text-right font-semibold text-gray-900 dark:text-gray-100">${Number(transferTotal || 0).toLocaleString('en-US')}</div>
                          </>
                        )}
                        <div className="text-gray-700 dark:text-gray-200 font-medium">Impacto en caja</div>
                        <div className="text-right font-semibold text-gray-900 dark:text-gray-100">${cashNeeded.toLocaleString('en-US')}</div>
                      </div>
                    );
                  })()}
                </div>
              )}
              {!viewOnlyMode && validationErrors && validationErrors.length > 0 && (
                <div className="text-[12px] text-amber-700 dark:text-amber-300">
                  {validationErrors[0]}
                </div>
              )}
              <div className="flex justify-end gap-2">
                <button type="button" onClick={closePrimaryModal} className="border px-3 py-1 rounded dark:border-gray-600 dark:text-gray-200">Cancelar</button>
                {!viewOnlyMode && (
                  <button
                    type="submit"
                    disabled={savingTx || (validationErrors && validationErrors.length > 0)}
                    className="bg-blue-600 text-white px-3 py-1 rounded disabled:opacity-50 transition-colors hover:bg-blue-700"
                  >
                    {editingTransactionId ? 'Guardar cambios' : 'Guardar'}
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      )}

      {addDepositModal.show && (
        <div className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-semibold text-gray-900 dark:text-white">Agregar depósito</h3>
              <button onClick={() => setAddDepositModal(createEmptyAddDepositModal())} className="p-2 hover:bg-gray-100 hover:dark:bg-gray-700 rounded-lg"><X className="text-gray-700 dark:text-gray-300" /></button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="block text-sm text-gray-700 dark:text-gray-200">Fecha</label>
                <input type="date" value={addDepositModal.date} onChange={(e) => setAddDepositModal(prev=>({ ...prev, date: e.target.value }))} className="w-full border p-2 rounded border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white" />
              </div>
              <div>
                <label className="block text-sm text-gray-700 dark:text-gray-200">Monto</label>
                <input type="number" value={addDepositModal.amount} onChange={(e) => setAddDepositModal(prev=>({ ...prev, amount: e.target.value }))} className="w-full border p-2 rounded border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white" />
              </div>
              <div>
                <label className="block text-sm text-gray-700 dark:text-gray-200">Referencia (texto)</label>
                <input type="text" value={addDepositModal.reference} onChange={(e) => setAddDepositModal(prev=>({ ...prev, reference: e.target.value }))} className="w-full border p-2 rounded border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white" />
              </div>
              <div>
                <label className="block text-sm text-gray-700 dark:text-gray-200">Comprobante (archivo)</label>
                <input
                  type="file"
                  accept="image/*,application/pdf"
                  onChange={(e) => setAddDepositModal(prev => ({ ...prev, proofFile: (e.target.files && e.target.files[0]) || null }))}
                  className="w-full border p-2 rounded border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
                {addDepositModal.proofFile && (
                  <div className="mt-1 text-xs text-gray-500 dark:text-gray-300 truncate">
                    {addDepositModal.proofFile.name} ({Math.round((addDepositModal.proofFile.size || 0)/1024)} KB)
                  </div>
                )}
              </div>
              <div className="flex justify-end gap-2">
                <button onClick={() => setAddDepositModal(createEmptyAddDepositModal())} className="border px-3 py-1 rounded dark:border-gray-600 dark:text-gray-200">Cancelar</button>
                <button onClick={handleAddDeposit} className="bg-green-600 text-white px-3 py-1 rounded">Agregar</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {depositHistoryModal.show && depositHistoryModal.transaction && (
        <div className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded p-6 w-full max-w-lg">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-semibold text-gray-900 dark:text-white">Depósitos - Transacción #{depositHistoryModal.transaction.id}</h3>
              <button onClick={() => setDepositHistoryModal({ show: false, transaction: null })} className="p-2 hover:bg-gray-100 hover:dark:bg-gray-700 rounded-lg"><X className="text-gray-700 dark:text-gray-300" /></button>
            </div>
            <div className="space-y-3 max-h-[60vh] overflow-y-auto">
              {(depositHistoryModal.transaction.deposits || []).length === 0 ? (
                <p className="text-sm text-gray-500 dark:text-gray-300">No hay depósitos registrados para esta operación.</p>
              ) : (
                (depositHistoryModal.transaction.deposits || []).map(d => (
                  <div key={d.id} className="p-3 border rounded flex justify-between items-center border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700">
                    <div>
                      <div className="font-medium text-gray-900 dark:text-white">Monto: ${Number(d.amount || 0).toLocaleString('en-US')}</div>
                      <div className="text-xs text-gray-500 dark:text-gray-300">Fecha: {d.date || d.deposit_date || '—'}</div>
                      {d.reference && (
                        <div className="text-xs text-gray-500 dark:text-gray-300">Ref: {d.reference}</div>
                      )}
                    </div>
                    <div className="flex flex-col items-end">
                      {(() => {
                        const url = d?.metadata?.proof_url || d?.proof || null;
                        return url ? (
                          <a href={url} target="_blank" rel="noreferrer" className="text-blue-600 dark:text-blue-400 text-sm">Ver comprobante</a>
                        ) : (
                          <span className="text-sm text-gray-500 dark:text-gray-300">Sin comprobante</span>
                        );
                      })()}
                      <button className="text-red-600 dark:text-red-400 text-sm mt-2" onClick={() => handleDeleteDepositLocal(depositHistoryModal.transaction, d.id)}>Eliminar</button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {deliveryModal.show && (
        <div className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-xl w-full max-w-lg">
            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
              {(() => { const tx = transactions.find(t => t.id === deliveryModal.transactionId); const isOrderLike = tx?.type === 'order' || tx?.type === 'transfer'; return (
                <h3 className="text-lg font-semibold text-gray-800 dark:text-white">{isOrderLike ? 'Marcar como recibido' : 'Marcar como entregado'}</h3>
              ); })()}
              <button onClick={() => setDeliveryModal({ show: false, transactionId: null, amount: '' })} className="p-2 hover:bg-gray-100 hover:dark:bg-gray-700 rounded-lg"><X className="w-5 h-5 text-gray-600 dark:text-gray-300" /></button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Monto recibido</label>
                <input type="number" min="0" value={deliveryModal.amount || ''} onChange={(e) => setDeliveryModal(prev=>({ ...prev, amount: e.target.value }))} className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white" placeholder="Ej: 1006000" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Comprobante (imagen o PDF)</label>
                <input type="file" accept="image/*,.pdf" onChange={(e) => setProofFile(e.target.files?.[0] || null)} className="w-full text-gray-900 dark:text-gray-200" />
              </div>
              <div className="flex justify-end gap-2">
                <button onClick={() => setDeliveryModal({ show: false, transactionId: null, amount: '' })} className="px-4 py-2 rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 hover:dark:bg-gray-700">Cancelar</button>
                {(() => { const tx = transactions.find(t => t.id === deliveryModal.transactionId); const isOrderLike = tx?.type === 'order' || tx?.type === 'transfer'; return (
                  <button onClick={() => attemptConfirmDelivery()} className="px-4 py-2 rounded-md bg-green-600 text-white hover:bg-green-700 disabled:opacity-50" disabled={!deliveryModal.amount}>{isOrderLike ? 'Confirmar recibido' : 'Confirmar entrega'}</button>
                ); })()}
              </div>
            </div>
          </div>
        </div>
      )}

      {deliveryConfirmModal.show && (
        <div className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-xl w-full max-w-md p-6">
            <div className="mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Confirmar diferencia de monto</h3>
              <p className="text-sm text-gray-600 dark:text-gray-300 mt-2">El monto recibido difiere del monto esperado. Confirma antes de aplicar los cambios.</p>
            </div>
            <div className="space-y-2 mb-4">
              <div className="text-sm text-gray-900 dark:text-white">Esperado: <span className="font-medium">${Number(deliveryConfirmModal.expected || 0).toLocaleString('en-US')}</span></div>
              <div className="text-sm text-gray-900 dark:text-white">Recibido: <span className="font-medium">${Number(deliveryConfirmModal.received || 0).toLocaleString('en-US')}</span></div>
              <div className="text-sm text-gray-900 dark:text-white">Diferencia: <span className="font-medium">${Number((deliveryConfirmModal.received || 0) - (deliveryConfirmModal.expected || 0)).toLocaleString('en-US')}</span></div>
            </div>
            <div className="flex justify-end gap-2">
              <button onClick={() => setDeliveryConfirmModal({ show: false, expected: null, received: null })} className="px-4 py-2 rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 hover:dark:bg-gray-700">Volver</button>
              <button onClick={() => { handleConfirmDelivery(); setDeliveryConfirmModal({ show: false, expected: null, received: null }); }} className="px-4 py-2 rounded-md bg-green-600 text-white hover:bg-green-700">Confirmar y aplicar</button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default Transactions;
